import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  addEdge, useNodesState, useEdgesState, Handle, Position,
  BaseEdge, EdgeLabelRenderer, getBezierPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Image as ImageIcon, Video, Type, Sparkles, Loader2, X,
  Download, Plus, Upload, ArrowLeft, Scissors, MessageSquare,
  Search, CheckCircle, CircleAlert, User, Box, Boxes, Play, Wand2, Clock, RefreshCw, Shield, Pencil, Pin,
  ChevronDown, Check, SlidersHorizontal, Volume2, FolderOpen, Save,
} from 'lucide-react';
import api from '../utils/api';
import useTheme from '../hooks/useTheme';
import useCanvasAgent from '../hooks/useCanvasAgent';
import CanvasAgentPanel from '../components/canvas-agent/CanvasAgentPanel';
import CanvasAssetPanel from '../components/canvas-asset/CanvasAssetPanel';
import NodePromptEditor, { promptDocFromText, reconcileReferenceBindings, remapPromptText } from './canvas/NodePromptEditor';
import ReferenceMediaGroup from './canvas/ReferenceMediaGroup';

// ── Node 样式
const nodeCls = (selected) =>
  `rounded-lg overflow-hidden shadow-[0_14px_34px_rgba(76,64,44,0.12)] border transition-all duration-150 min-w-[190px] max-w-[240px] cursor-default
   bg-[#fdfbf6] dark:bg-[#18181f] text-gray-900 dark:text-white ${
    selected
      ? 'border-[#8c7db0]/80 shadow-[#8c7db0]/20'
      : 'border-[#d8d1c2] dark:border-white/[0.08]'
  }`;

const hdrCls = 'flex items-center gap-1.5 px-3 py-2 border-b border-[#ded6c8] dark:border-white/[0.06] text-[11px] text-stone-500 dark:text-gray-500';
const handleCls = '!w-3 !h-3 !bg-[#8c7db0] !border-[1.5px] !border-[#c9bedc] !rounded-full';
const placeholderCls = 'flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-600 py-10';

// ── 图片节点
function ImageNode({ id, data, selected }) {
  const [imgErr, setImgErr] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(data.label || '');
  const [assetMenu, setAssetMenu] = useState(false);
  const isAsset = data.agent_role === 'asset';
  useEffect(() => { setImgErr(false); }, [data.url]);
  useEffect(() => { setName(data.label || ''); }, [data.label]);
  const commitName = () => {
    setEditing(false);
    const v = name.trim();
    if (v !== (data.label || '')) window.dispatchEvent(new CustomEvent('canvas-node-rename', { detail: { id, label: v } }));
  };
  const markAsset = (type) => {
    setAssetMenu(false);
    window.dispatchEvent(new CustomEvent('canvas-node-set-asset', { detail: { id, name: (data.label || '').trim(), type } }));
  };
  // Seedance 合规校验状态（触发在节点右键菜单里，对标 libtv）
  const liveness = data.liveness_status || null;

  const loadingImg = (data.status === 'generating' || data.status === 'processing') && !data.url;
  const hasImage = Boolean(data.url) && !imgErr && !loadingImg;
  const showChrome = selected || editing; // 选中/改名常显；否则悬停浮现

  return (
    <div className={`group relative rounded-lg overflow-hidden border transition-all duration-150 min-w-[190px] max-w-[240px] cursor-default shadow-[0_10px_28px_rgba(76,64,44,0.10)] ${
      selected ? 'border-[#8c7db0]/80 shadow-[#8c7db0]/20' : 'border-black/[0.06] dark:border-white/[0.06]'
    }`}>
      <Handle type="target" position={Position.Left} className={handleCls} />
      <Handle type="source" position={Position.Right} className={handleCls} />

      {/* 主体：图片占满，不给顶部信息留固定空间 → 脸永远不被盖 */}
      {loadingImg
        ? <div className="relative w-full overflow-hidden" style={{ height: 150 }}>
            <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-stone-200 via-stone-100 to-stone-200 dark:from-white/[0.06] dark:via-white/[0.03] dark:to-white/[0.06]" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-stone-400">
              <Loader2 size={22} className="animate-spin text-[#8c7db0]" /><span className="text-[10px]">生成中…</span>
            </div>
          </div>
        : hasImage
          ? <img src={data.url} alt="" draggable={false}
              className="w-full object-cover object-top block bg-[#fdfbf6] dark:bg-[#18181f]" style={{ maxHeight: 220 }}
              onError={() => setImgErr(true)}
            />
          : <div className={`${placeholderCls} bg-[#fdfbf6] dark:bg-[#18181f]`}>
              {data.url && imgErr
                ? <><ImageIcon size={28} strokeWidth={1} /><span className="text-[11px] text-red-400">加载失败</span></>
                : <><ImageIcon size={28} strokeWidth={1} /><span className="text-[11px]">选中后在下方生成或上传</span></>
              }
            </div>
      }

      {/* 顶部右上角操作：悬停/选中才浮现，只占右上角尽量不挡脸 */}
      {!loadingImg && (
        <div className={`nodrag absolute right-1.5 top-1.5 flex items-center gap-1 transition-opacity duration-150 ${showChrome ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          {isAsset ? (
            <span className="rounded bg-black/55 px-1.5 py-0.5 text-[9px] text-white backdrop-blur-sm" title="已是素材">素材</span>
          ) : (
            <div className="relative">
              <button type="button" title="设为素材（钉成角色/场景/道具/特效）" onClick={e => { e.stopPropagation(); setAssetMenu(v => !v); }}
                className="flex h-5 w-5 items-center justify-center rounded bg-black/55 text-white backdrop-blur-sm hover:bg-black/70"><Pin size={11} /></button>
              {assetMenu && (
                <div className="absolute right-0 top-full z-20 mt-1 w-16 overflow-hidden rounded-md border border-stone-200 bg-white shadow-lg dark:border-white/10 dark:bg-[#1a1a22]" onClick={e => e.stopPropagation()}>
                  {[['character', '角色'], ['scene', '场景'], ['prop', '道具'], ['fx_move', '特效']].map(([t, l]) => (
                    <button key={t} type="button" onClick={() => markAsset(t)}
                      className="block w-full px-2 py-1 text-left text-[10px] text-stone-700 hover:bg-[#f3f0f8] dark:text-gray-300 dark:hover:bg-white/[0.06]">{l}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {data.status === 'generating' && <span className="flex h-5 w-5 items-center justify-center rounded bg-black/55 text-white"><Loader2 size={11} className="animate-spin" /></span>}
          {data.status === 'done'       && <span className="flex h-5 items-center rounded bg-emerald-500/85 px-1 text-[10px] font-bold text-white">✓</span>}
          {data.status === 'failed'     && <span className="flex h-5 items-center rounded bg-red-500/85 px-1 text-[10px] font-bold text-white">!</span>}
        </div>
      )}


      {/* 底部名字条：常显，落在画面底部(脚那侧)绝不挡脸；双击改名 */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/65 via-black/25 to-transparent px-2 py-1 text-[11px] text-white">
        <ImageIcon size={10} className="shrink-0 opacity-70" />
        {editing ? (
          <input autoFocus value={name} onChange={e => setName(e.target.value)} onBlur={commitName}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitName(); } if (e.key === 'Escape') { setEditing(false); setName(data.label || ''); } }}
            onClick={e => e.stopPropagation()}
            placeholder="给这张图起个名，如 叶长歌"
            className="nodrag flex-1 min-w-0 rounded bg-white/95 px-1 text-[10px] text-stone-800 outline-none" />
        ) : (
          <>
            <span className="truncate cursor-text" title="双击改名" onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}>{data.label || '图片'}</span>
            {/* Seedance 合规校验角标：紧跟文件名（对标 libtv）。蓝盾=可用于生成 */}
            {liveness === 'active' && (
              <Shield size={11} className="shrink-0 fill-[#4a90d9] text-[#4a90d9]" title="素材内容已合规，可用于 Seedance2.0 视频生成" />
            )}
            {liveness === 'processing' && (
              <Loader2 size={10} className="shrink-0 animate-spin opacity-80" title="Seedance2.0 合规校验中…" />
            )}
            {liveness === 'failed' && (
              <Shield size={11} className="shrink-0 fill-rose-500 text-rose-500" title="未通过合规校验（可能被判定为真人肖像）——右键可重试" />
            )}
            <span className="flex-1" />
            <button type="button" title="改名" onClick={e => { e.stopPropagation(); setEditing(true); }}
              className="nodrag shrink-0 opacity-0 transition-opacity group-hover:opacity-80 hover:!opacity-100"><Pencil size={9} /></button>
          </>
        )}
      </div>
    </div>
  );
}

// ── 视频节点
function VideoNode({ id, data, selected }) {
  return (
    <div className={nodeCls(selected)}>
      <Handle type="target" position={Position.Left} className={handleCls} />
      {/* 右输出口：已生成的片段可连给下一个视频节点当参考视频（延长/编辑/风格运镜，靠提示词表达） */}
      <Handle type="source" position={Position.Right} className={handleCls} />
      <div className={hdrCls}>
        <Video size={10} className="shrink-0" />
        <span className="truncate flex-1">{data.label || '视频'}</span>
        {data.status === 'processing' && (
          <span className="flex items-center gap-1 text-blue-400 shrink-0 text-[10px]">
            <Loader2 size={9} className="animate-spin" />生成中
          </span>
        )}
        {data.status === 'succeeded' && !(data.archive_error && data.storage !== 'tos_presigned') && <span className="text-emerald-400 shrink-0">✓</span>}
        {data.status === 'failed'    && <span className="text-red-400 shrink-0">!</span>}
        {/* 存下来失败了：片段现在还挂在厂商临时链接上（约24小时失效），失效后这个节点就是死链。
            不写"转存/归档"这种内部词——用户看不懂也没法判断，只告诉他要做什么（重新上传）。
            带 storage 条件是防旧失败标记残留：已经存好了就不该还挂个红叹号。 */}
        {data.archive_error && data.storage !== 'tos_presigned' && (
          <span
            className="nodrag shrink-0 text-red-500"
            title="这段视频没能存进我们的存储，24 小时后会失效。请重新上传或重新生成一次。"
          >
            <CircleAlert size={10} />
          </span>
        )}
      </div>
      {data.url
        ? <video src={data.url} controls className="w-full block" style={{ maxHeight: 200 }} />
        : (data.status === 'processing' || data.status === 'generating')
          ? <div className="relative w-full overflow-hidden" style={{ height: 150 }}>
              <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-stone-300 via-stone-100 to-stone-300" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-stone-500">
                <Loader2 size={22} className="animate-spin text-blue-400" /><span className="text-[10px]">视频生成中…</span>
              </div>
            </div>
          : <div className={placeholderCls}><Video size={28} strokeWidth={1} /><span className="text-[11px]">连接图片/音频/视频节点后生成</span></div>
      }
    </div>
  );
}

// ── 音频节点
function AudioNode({ data, selected }) {
  return (
    <div className={`${nodeCls(selected)} min-w-[240px]`}>
      <Handle type="target" position={Position.Left} className={handleCls} />
      <Handle type="source" position={Position.Right} className={handleCls} />
      <div className={hdrCls}>
        <Volume2 size={11} className="shrink-0 text-[#8c7db0]" />
        <span className="truncate flex-1">{data.label || '音频'}</span>
        {data.voice_id && <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[9px] text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">声线已锁定</span>}
        {(data.status === 'generating' || data.status === 'uploading') && <Loader2 size={10} className="animate-spin text-[#8c7db0]" />}
        {data.status === 'done' && <span className="text-emerald-500">✓</span>}
      </div>
      {data.url ? (
        <div className="space-y-2 px-3 py-3">
          <audio src={data.url} controls preload="metadata" className="nodrag nowheel h-9 w-full" />
          {data.transcript && <p className="line-clamp-2 text-[11px] leading-4 text-stone-500 dark:text-gray-400">{data.transcript}</p>}
          <div className="flex items-center justify-between text-[9px] text-stone-400 dark:text-gray-600">
            <span>{data.source === 'voice_clone' ? '固定声线' : data.source === 'tts' ? '文字转语音' : '上传音频'}</span>
            <span>{data.duration_ms ? `${(data.duration_ms / 1000).toFixed(1)}s` : (data.format || '').toUpperCase()}</span>
          </div>
        </div>
      ) : (
        <div className={placeholderCls}>
          <Volume2 size={28} strokeWidth={1} />
          <span className="text-[11px]">上传样本或输入台词生成</span>
        </div>
      )}
    </div>
  );
}

// ── 文字节点
function TextNode({ id, data, selected }) {
  return (
    <div className={`${nodeCls(selected)} min-w-[200px] max-w-[320px]`}>
      <Handle type="target" position={Position.Left} className={handleCls} />
      <Handle type="source" position={Position.Right} className={handleCls} />
      <div className={hdrCls}>
        <Type size={10} className="shrink-0" />
        <span className="truncate flex-1">{data.label || '文本'}</span>
      </div>
      <div className="px-3 py-3 text-[13px] leading-relaxed whitespace-pre-wrap min-h-[60px] text-gray-700 dark:text-gray-300">
        {data.content || <span className="text-gray-300 dark:text-gray-600 italic">选中后在下方输入文字</span>}
      </div>
    </div>
  );
}

function GroupNode({ data, selected }) {
  return (
    <div
      className={`rounded-[18px] border border-dashed bg-[#eee9de]/40 dark:bg-white/[0.04] px-5 py-4 pointer-events-none ${
        selected ? 'border-[#8c7db0]/70' : 'border-[#c9bedc]/70'
      }`}
      style={{ width: data.width || 1380, height: data.height || 360 }}
    >
      <div className="inline-flex items-center gap-2 rounded-lg border border-[#c9bedc] bg-[#fbf8f1]/90 px-3 py-1.5 text-[13px] font-semibold text-[#6f6196] shadow-sm">
        <span>{data.label || '场次组'}</span>
        {data.summary && <span className="text-[11px] font-normal text-[#8c7db0]">{data.summary}</span>}
      </div>
    </div>
  );
}

// ── 3D 导演台节点（实际编辑器只在大弹窗中加载，避免 three.js 进入主前端包）
function Pose3DNode({ id, data, selected }) {
  const [imgErr, setImgErr] = useState(false);
  const latestCapture = data.captures?.[data.captures.length - 1];
  useEffect(() => { setImgErr(false); }, [latestCapture?.url]);

  return (
    <div className={`${nodeCls(selected)} w-[220px]`}>
      <Handle type="target" position={Position.Left} className={handleCls} />
      <Handle type="source" position={Position.Right} className={handleCls} />
      <div className={`${hdrCls} text-[#76549b] dark:text-[#c7a8ef]`}>
        <Boxes size={13} />
        <span className="font-medium">{data.label || '3D导演台'}</span>
        <span className="ml-auto rounded bg-[#efe6fa] px-1.5 py-0.5 text-[9px] dark:bg-[#8f62bd]/20">构图</span>
      </div>
      {latestCapture?.url && !imgErr ? (
        <img
          src={latestCapture.url}
          alt="最近一次3D构图截图"
          draggable={false}
          className="block h-[132px] w-full bg-stone-100 object-cover dark:bg-black/20"
          onError={() => setImgErr(true)}
        />
      ) : (
        <div className="flex h-[132px] flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#f3edf9] to-[#faf6ef] text-[#8a72a7] dark:from-[#241d2d] dark:to-[#191820] dark:text-[#a78bc8]">
          <Boxes size={30} strokeWidth={1.25} />
          <span className="text-[11px]">摆小人 · 定机位 · 出参考图</span>
        </div>
      )}
      <div className="p-2.5">
        <button
          type="button"
          className="nodrag nowheel flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#76549b] px-3 py-2 text-[11px] font-medium text-white transition-colors hover:bg-[#654486]"
          onClick={(event) => {
            event.stopPropagation();
            window.dispatchEvent(new CustomEvent('canvas-open-pose3d', { detail: { id } }));
          }}
        >
          <Boxes size={13} /> 打开导演台
        </button>
      </div>
    </div>
  );
}

const nodeTypes = { groupNode: GroupNode, imageNode: ImageNode, videoNode: VideoNode, audioNode: AudioNode, textNode: TextNode, pose3dNode: Pose3DNode };

// ── 自定义连线：贝塞尔曲线 + 中点剪刀删除按钮
function CuttableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data }) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd}
        style={{ stroke: '#8c7db0', strokeWidth: 1.5 }} />
      <EdgeLabelRenderer>
        <button
          onClick={(e) => { e.stopPropagation(); data?.onCut?.(id); }}
          title="删除连线"
          className="nodrag nopan absolute w-6 h-6 rounded-full flex items-center justify-center
                     bg-white dark:bg-[#23232c] border border-gray-300 dark:border-white/15 shadow-md
                     text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:border-red-500/40
                     hover:scale-110 transition-all"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
        >
          <Scissors size={12} />
        </button>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = { cuttable: CuttableEdge };

const edgeOptions = {
  type: 'cuttable',
  animated: true,
};

// ── 选项常量
const RATIOS    = ['9:16', '16:9', '4:3', '1:1', '3:4', '21:9'];
// Seedance 2.0 系列官方规格：4~15 秒任意整数（旧值含 3 秒，Seedance 不支持、后端会静默夹到 4；
// 且缺 6/7/9 等 Agent 现在会按台词量给出的精确秒数，导致选不中当前值）
const DURATIONS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

// ── 图类型（人物模型用，后续由画布内项目素材资料夹承载）
const VIEWS = [
  { label: '自由',     key: 'free',     prompt: '',                                                                              size: null },
  { label: '三视图',   key: 'triple',   prompt: '同一人物三个角度写真（正面·45度侧面·背面），纯白背景，全身自然站姿，服装完整，三图横向并排', size: '3072x1920' },
  { label: '全身正面', key: 'front',    prompt: '全身正面人物写真，纯白背景，自然站姿，服装全貌清晰',                                  size: '1440x2560' },
  { label: '半身特写', key: 'halfbody', prompt: '半身正面人物写真特写，纯白背景，五官精致，上半身服装细节完整',                          size: '1920x2560' },
];

// 生图比例 → 代表尺寸(WxH)。ark/dashscope 用此尺寸出图；google 由尺寸推出 aspectRatio
const RATIO_SIZE = {
  '9:16': '1440x2560', '16:9': '2560x1440', '1:1': '2048x2048',
  '3:4': '1536x2048', '4:3': '2048x1536', '2:3': '1440x2160', '3:2': '2160x1440',
};
const IMG_RATIOS = ['9:16', '16:9', '1:1', '3:4', '4:3', '2:3', '3:2'];
const SIZE_RATIO = Object.fromEntries(Object.entries(RATIO_SIZE).map(([r, s]) => [s, r]));
// 参考视频计费用：我们自己生成的片段只存了 resolution 档，按档反推像素算输入 token。
// 与后端 canvas.js 的 RESOLUTION_PIXELS 保持一致（两边都算价，差一位就是显示价≠扣款价）。
const VIDEO_RESOLUTION_PIXELS = {
  '480p': [854, 480], '720p': [1280, 720], '1080p': [1920, 1080],
  '480P': [854, 480], '720P': [1280, 720], '1080P': [1920, 1080],
};

// ── 记住上次生成配置：新建的空节点默认沿用你上次用的模型/比例/画质，不用每次重调（跨会话、账号级）。
// 只对"没有自己配置的节点"生效；已经生成过、data 里存了 model 的旧节点仍读它自己的。
const LAST_IMG_CFG_KEY = 'xy:canvas:lastImageConfig';
const LAST_VID_CFG_KEY = 'xy:canvas:lastVideoConfig';
function readLastConfig(key) {
  try { return JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch { return {}; }
}
function writeLastConfig(key, cfg) {
  try { localStorage.setItem(key, JSON.stringify(cfg || {})); } catch { /* localStorage 不可用则忽略 */ }
}

// ── 生成栏通用小部件（模型/设置 chip + 弹层 + 药丸）──
function GenChip({ icon: Icon, active, onClick, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] transition-colors ${active
        ? 'border-[#8c7db0] bg-[#eee9de] text-[#6f6196] dark:border-white/20 dark:bg-white/[0.08] dark:text-[#b9afe7]'
        : 'border-[#ded6c8] bg-[#f7f3eb] text-gray-600 hover:border-[#c9bedc] dark:border-white/[0.08] dark:bg-black/25 dark:text-gray-300'}`}>
      {Icon && <Icon size={13} className="shrink-0 text-[#6f6196] dark:text-[#b9afe7]" />}
      <span className="truncate max-w-[180px]">{children}</span>
      <ChevronDown size={11} className="shrink-0 opacity-60" />
    </button>
  );
}
function GenPopover({ open, onClose, children, className = '' }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div className={`absolute bottom-full left-0 z-30 mb-2 max-h-[min(60vh,360px)] overflow-y-auto rounded-xl border border-[#ded6c8] bg-[#fbfaf6] p-2 shadow-xl dark:border-white/10 dark:bg-[#1b1b21] ${className}`}>
        {children}
      </div>
    </>
  );
}
function GenPill({ active, onClick, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`rounded-lg border px-2.5 py-1 text-[12px] transition-colors ${active
        ? 'border-[#8c7db0] bg-[#eee9de] text-[#6f6196] dark:border-white/25 dark:bg-white/[0.12] dark:text-[#c9bef0]'
        : 'border-[#ded6c8] bg-[#f7f3eb] text-gray-500 hover:border-[#c9bedc] dark:border-white/[0.08] dark:bg-black/20 dark:text-gray-400'}`}>
      {children}
    </button>
  );
}

// ── DB → ReactFlow 转换
const toFlowNode = (n) => ({
  id:       n.id,
  type:     n.type === 'group' ? 'groupNode' : n.type === 'image' ? 'imageNode' : n.type === 'audio' ? 'audioNode' : n.type === 'text' ? 'textNode' : n.type === 'pose3d' ? 'pose3dNode' : 'videoNode',
  position: { x: n.x, y: n.y },
  data:     n.data || {},
  draggable: n.type !== 'group',
  selectable: n.type !== 'group',
  zIndex: n.type === 'group' ? -1 : 1,
});
const toFlowEdge = (e) => ({
  id: e.id, source: e.source_node_id, target: e.target_node_id, ...edgeOptions,
});

// ── 主组件
export default function CanvasPage() {
  const { id: canvasId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const [canvasName, setCanvasName]       = useState('');
  const [editingName, setEditingName]     = useState(false);
  const [nodes, setNodes, onNodesChange]  = useNodesState([]);
  const [edges, setEdges, onEdgesChange]  = useEdgesState([]);
  const [selectedId, setSelectedId]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [addMenuOpen, setAddMenuOpen]     = useState(false);
  // 节点右键菜单（对标 libtv：合规校验/存资产/复制 都收在右键里）
  const [nodeMenu, setNodeMenu] = useState(null); // { x, y, node }
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [assetPanelOpen, setAssetPanelOpen] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryFilter, setDirectoryFilter] = useState('all');
  const [directorNodeId, setDirectorNodeId] = useState(null);
  const [directorBusy, setDirectorBusy] = useState(false);
  const [directorError, setDirectorError] = useState('');
  const [directorAgentInput, setDirectorAgentInput] = useState('');
  const [directorAgentLoading, setDirectorAgentLoading] = useState(false);
  const [directorAgentReply, setDirectorAgentReply] = useState('');
  const [directorAgentHistory, setDirectorAgentHistory] = useState([]);
  const [directorAgentHistoryOpen, setDirectorAgentHistoryOpen] = useState(false);
  const [directorClosing, setDirectorClosing] = useState(false);
  const rfRef = useRef(null);
  const directorIframeRef = useRef(null);
  const directorExportResolverRef = useRef(null);
  const directorExportSequenceRef = useRef(0);
  const directorNodeWriteQueueRef = useRef(new Map());
  const productionPollRef = useRef(null); // 开拍后的增量轮询句柄
  const agentLoadRef = useRef(null); // 持有 canvasAgent.load，供轮询里重新拉对话（取回"素材怎么补"这类弹选择消息）

  // 记住上次配置：初值优先读上次用过的（读不到再回退写死默认）
  const lastImgCfg = useRef(readLastConfig(LAST_IMG_CFG_KEY)).current;
  const lastVidCfg = useRef(readLastConfig(LAST_VID_CFG_KEY)).current;

  // 面板状态
  const [imgPrompt,   setImgPrompt]   = useState('');
  const [imgViewIdx,  setImgViewIdx]  = useState(0);
  const [imgSize,     setImgSize]     = useState(lastImgCfg.size || '1440x2560');
  const [imgTab,      setImgTab]      = useState('generate'); // 'generate' | 'upload'
  const [vidPrompt,   setVidPrompt]   = useState('');
  const [vidRatio,    setVidRatio]    = useState(lastVidCfg.ratio || '9:16');
  const [vidDur,      setVidDur]      = useState(lastVidCfg.duration || 5);
  const [txtContent,  setTxtContent]  = useState('');
  const [audText,     setAudText]     = useState('');
  const [audModel,    setAudModel]    = useState('qwen-audio-tts-flash');
  const [audVoice,    setAudVoice]    = useState('longanhuan_v3.6');
  const [voiceRightsConfirmed, setVoiceRightsConfirmed] = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [archivingRefVideo, setArchivingRefVideo] = useState(false); // 参考视频补存中
  const [genErr,      setGenErr]      = useState('');
  const [agentPrompt, setAgentPrompt] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentErr, setAgentErr] = useState('');
  const [agentNotice, setAgentNotice] = useState('');
  const [agentAnalysis, setAgentAnalysis] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [assetRefLoading, setAssetRefLoading] = useState(false);
  const [keyframeLoading, setKeyframeLoading] = useState(false);
  const [videoSubmitLoading, setVideoSubmitLoading] = useState(false);
  const [productionPlan, setProductionPlan] = useState(null);
  const [productionPlanLoading, setProductionPlanLoading] = useState(false);
  const [generatingGapIds, setGeneratingGapIds] = useState(() => new Set());
  const [me, setMe] = useState(null);
  const [planRefreshVersion, setPlanRefreshVersion] = useState(0);
  const [deliveryManifest, setDeliveryManifest] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const showLegacyAgentPanel = Boolean(window.__XIAOYING_LEGACY_CANVAS_AGENT__);
  const uploadRef = useRef(null);
  const audioUploadRef = useRef(null);
  const videoUploadRef = useRef(null);

  // 模型注册表（后端配置驱动）+ 当前选择
  const [models,   setModels]   = useState({ image: [], video: [], audio: [] });
  const [artStyles, setArtStyles] = useState([]);
  const [agentStyle, setAgentStyle] = useState(''); // 美术风格 key，空=按剧本题材自动判断
  const [imgModel, setImgModel] = useState(lastImgCfg.model || 'seedream');
  const [vidModel, setVidModel] = useState(lastVidCfg.model || 'seedance');
  const [vidRes,   setVidRes]   = useState(lastVidCfg.resolution || '720p'); // 视频清晰度
  const [vidAudio, setVidAudio] = useState(true);
  const [imgMenu, setImgMenu] = useState(null); // 生图底栏弹层：null|'model'|'settings'
  const [vidMenu, setVidMenu] = useState(null); // 生视频底栏弹层：null|'model'|'settings'
  const [imgQuality, setImgQuality] = useState(lastImgCfg.quality || '2K'); // Nano Banana 画质档（记住用户所选）
  // 兜底：存储的旧 key 若已不在列表，回退到第一个有效模型
  const imgModelVal = models.image.find(m => m.key === imgModel)?.key || models.image[0]?.key || '';
  const vidModelVal = models.video.find(m => m.key === vidModel)?.key || models.video[0]?.key || '';
  const imgSpec = models.image.find(m => m.key === imgModelVal);
  const vidSpec = models.video.find(m => m.key === vidModelVal);
  const audModelVal = models.audio?.find(m => m.key === audModel)?.key || models.audio?.[0]?.key || '';
  const audSpec = models.audio?.find(m => m.key === audModelVal);
  const audVoiceVal = audSpec?.voices?.some(voice => voice.key === audVoice) ? audVoice : (audSpec?.defaultVoice || audSpec?.voices?.[0]?.key || '');
  // 画质档钳制到当前模型支持范围（不支持画质档的模型为 null）；点数按档取，缺省固定 cost
  const imgQualityVal = imgSpec?.sizes
    ? (imgSpec.sizes.includes(imgQuality) ? imgQuality : (imgSpec.defaultSize || imgSpec.sizes[imgSpec.sizes.length - 1]))
    : null;
  const imgCost = imgSpec ? (imgSpec.creditsBySize?.[imgQualityVal] ?? imgSpec.cost) : 0;
  const imgRatioVal = SIZE_RATIO[imgSize] || '9:16';
  // 清晰度兜底到当前模型支持的档位
  const preferredVideoResolution = vidSpec?.resolutions?.[0] || vidSpec?.defaultRes || '';
  const vidResVal = (vidSpec?.resolutions || []).includes(vidRes) ? vidRes : preferredVideoResolution;
  const vidDurationMin = Number(vidSpec?.durationMin) || DURATIONS[0];
  const vidDurationMax = Number(vidSpec?.durationMax) || DURATIONS[DURATIONS.length - 1];
  const vidDurVal = Math.min(vidDurationMax, Math.max(vidDurationMin, Math.round(Number(vidDur) || 5)));
  const supportsReferenceAudio = vidSpec?.supportsReferenceAudio === true;
  const supportsReferenceVideo = vidSpec?.supportsReferenceVideo === true;
  const supportsGeneratedAudio = vidSpec?.supportsGeneratedAudio === true;
  // 输出部分点数 = ceil(点/秒 × 时长)。连了参考视频时还要加输入部分，见下方 vidCost。
  const vidOutputCost = vidSpec?.creditsPerSec?.[vidResVal] != null
    ? Math.ceil(vidSpec.creditsPerSec[vidResVal] * vidDurVal)
    : (vidSpec?.cost || 0);
  const selectedNode = nodes.find(n => n.id === selectedId) || null;
  const promptReferenceItems = useMemo(() => {
    if (!selectedNode || !['imageNode', 'videoNode'].includes(selectedNode.type)) return [];
    return edges
      .filter(edge => edge.target === selectedId)
      .map(edge => {
        const node = nodes.find(candidate => candidate.id === edge.source);
        const mediaType = node?.type === 'imageNode' ? 'image' : node?.type === 'audioNode' ? 'audio' : node?.type === 'videoNode' ? 'video' : null;
        if (!node || !mediaType) return null;
        return {
          edgeId: edge.id,
          nodeId: node.id,
          node,
          mediaType,
          label: node.data?.label || node.data?.asset?.name || node.data?.transcript || '',
        };
      })
      .filter(Boolean)
      .sort((a, b) => nodes.findIndex(node => node.id === a.nodeId) - nodes.findIndex(node => node.id === b.nodeId));
  }, [selectedNode, selectedId, edges, nodes]);
  const promptReferenceBindings = useMemo(() => reconcileReferenceBindings(
    selectedNode?.data?.reference_bindings,
    promptReferenceItems
  ), [selectedNode?.data?.reference_bindings, promptReferenceItems]);
  // 编号重排后，把编辑器正文里旧的 @图片N 同步改成新号（按 node_id 对齐），否则老节点里
  // Agent 写的"@图片2 是叶长歌"会因别名变了而显示"已失效"。仅在确有变化时改，避免无谓写状态。
  useEffect(() => {
    if (!selectedNode || !['imageNode', 'videoNode'].includes(selectedNode.type)) return;
    const stored = selectedNode.data?.reference_bindings;
    if (!Array.isArray(stored) || !stored.length) return;
    const setter = selectedNode.type === 'imageNode' ? setImgPrompt : setVidPrompt;
    setter(current => {
      const remapped = remapPromptText(current, stored, promptReferenceBindings);
      return remapped === current ? current : remapped;
    });
  }, [selectedId, promptReferenceBindings]); // eslint-disable-line react-hooks/exhaustive-deps
  const promptReferenceDisplayItems = useMemo(() => promptReferenceItems.map(item => {
    const binding = promptReferenceBindings.find(candidate => candidate.node_id === item.nodeId);
    return { ...item, bindingId: binding?.id, alias: binding?.alias || '素材', active: binding?.active !== false };
  }), [promptReferenceItems, promptReferenceBindings]);
  const refImgNodes  = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'videoNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'imageNode');
  }, [selectedNode, selectedId, edges, nodes]);
  // 片段节点除显式图片连线外，后端还会按所属分镜自动补入已验收资产；计费和参考总数必须把这部分也算上。
  const effectiveVideoRefImageNodes = useMemo(() => {
    const refs = refImgNodes.filter(node => node.data?.url && ['usable', 'locked'].includes(node.data?.qc_status));
    if (!selectedNode?.data?.shot_id || !['clip', 'video_clip'].includes(selectedNode.data?.agent_role)) return refs;
    const shot = nodes.find(node => node.data?.agent_role === 'shot_task' && node.data?.shot_id === selectedNode.data.shot_id);
    if (!shot) return refs;
    const normalize = value => String(value || '').replace(/[·・∙•‧\s　]/g, '');
    const assetRefs = shot.data?.asset_refs || shot.data?.assets_used || {};
    const referenceNames = new Set([
      ...(Array.isArray(assetRefs.characters) ? assetRefs.characters : []),
      ...(Array.isArray(assetRefs.scene) ? assetRefs.scene : []),
      ...(Array.isArray(assetRefs.props) ? assetRefs.props : []),
      ...(Array.isArray(assetRefs.fx_move) ? assetRefs.fx_move : []),
    ].map(normalize));
    const shotText = normalize(`${shot.data?.content || shot.data?.label || ''}${(shot.data?.dialogues || []).map(item => `${item.speaker || ''}:${item.text || ''}`).join('')}`);
    const inboundIds = new Set(edges.filter(edge => edge.target === shot.id).map(edge => edge.source));
    const seen = new Set(refs.map(node => node.id));
    const assets = nodes.filter(node => {
      if (seen.has(node.id) || node.data?.agent_role !== 'asset' || !node.data?.url) return false;
      if (node.data?.status !== 'accepted' || !['usable', 'locked'].includes(node.data?.qc_status)) return false;
      const name = normalize(node.data?.asset?.name || node.data?.label);
      return inboundIds.has(node.id) || (name && (referenceNames.has(name) || shotText.includes(name)));
    });
    return [...refs, ...assets];
  }, [refImgNodes, selectedNode, nodes, edges]);
  const refVideoAudioNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'videoNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'audioNode');
  }, [selectedNode, selectedId, edges, nodes]);
  // 连入的视频节点 = 参考视频（泛参考：视频编辑/延长/风格运镜靠用户在提示词里写清楚）
  const refVideoNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'videoNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'videoNode');
  }, [selectedNode, selectedId, edges, nodes]);
  // 空节点（还没生成过视频）和"生成了但没转存成功"是两回事，提示必须分开说：
  // 前者最常见（刚拖出来的新节点），跟它讲"归档"完全对不上号，用户只会一脸问号。
  const refVideoEmpty = useMemo(() => refVideoNodes.filter(n => !n.data?.url), [refVideoNodes]);
  // 参考视频最终必须落在我们的 TOS 上：厂商临时链接约 24h 失效，拿过期外链提交会白扣一轮点数。
  // 但"还没搬完"是纯内部状态——提交时后端 ensureReferenceVideosArchived 会自己补搬好，
  // 所以**不提示、不禁用按钮**（用户看不懂"转存/归档"，更没有判断依据该怎么办）。
  const refVideoUnarchived = useMemo(
    () => refVideoNodes.filter(n => n.data?.url && n.data?.storage !== 'tos_presigned'),
    [refVideoNodes]
  );
  // 真的搬失败过（archive_error 有值）才需要用户介入：这段片段大概率已经拿不回来，
  // 唯一可行动作是重新上传/重新生成。只有这种情况露出小红叹号。
  const refVideoArchiveFailed = useMemo(
    () => refVideoUnarchived.filter(n => n.data?.archive_error),
    [refVideoUnarchived]
  );
  // 参考视频的段数/时长上限（与后端 VIDEO_REF_LIMITS 对齐，火山官方规格）：
  // 最多 3 段、单段 ≤15s、合计 ≤15s。前端先拦，避免白等一轮提交被拒。
  const REF_VIDEO_MAX_CLIPS = 3;
  const REF_VIDEO_MAX_TOTAL_MS = 15000;
  const REF_VIDEO_MAX_CLIP_MS = 15000;
  // 每段的计费元数据。与后端 videoRefBillingMeta 同口径：
  // 上传的有 ffprobe 真实值；我们生成的只有 duration(秒)+resolution 档，按档反推像素、帧率兜 24。
  const refVideoMetas = useMemo(() => refVideoNodes.map(n => {
    const d = n.data || {};
    const [resW, resH] = VIDEO_RESOLUTION_PIXELS[d.resolution] || [];
    return {
      width: Number(d.width) || resW || 1280,
      height: Number(d.height) || resH || 720,
      fps: Number(d.fps) || 24,
      durationMs: Number(d.duration_ms) || (Number(d.duration) || 0) * 1000,
    };
  }), [refVideoNodes]);
  const refVideoTotalMs = useMemo(
    () => refVideoMetas.reduce((sum, m) => sum + m.durationMs, 0),
    [refVideoMetas]
  );
  const refVideoTooMany = refVideoNodes.length > REF_VIDEO_MAX_CLIPS;
  const refVideoTooLongTotal = refVideoTotalMs > REF_VIDEO_MAX_TOTAL_MS;
  const refVideoTooLongClip = refVideoMetas.some(m => m.durationMs > REF_VIDEO_MAX_CLIP_MS);
  const refVideoOverLimit = refVideoTooMany || refVideoTooLongTotal || refVideoTooLongClip;
  // 含参考视频时的点数，必须与后端 canvasModels.videoCost 一字不差，
  // 否则按钮显示一个价、实际扣另一个价。公式：
  //   输入 token = 宽×高×帧率×秒÷1024，点 = token × 元/百万token ÷1e6 × 15（1点=0.1元、售价=成本×1.5）
  //   含视频输入时火山整单改走输入档单价（约基准的 0.6），所以输出那部分也按输入档重算。
  const vidCost = useMemo(() => {
    const clips = refVideoMetas.filter(m => m.durationMs > 0);
    const base = vidSpec?.yuanPerMToken;
    const videoIn = vidSpec?.yuanPerMTokenVideoIn;
    const imagePoints = Math.max(0, effectiveVideoRefImageNodes.length - (Number(vidSpec?.referenceImageFreeCount) || 0))
      * (Number(vidSpec?.referenceImageCredits) || 0);
    const flatVideoInputRate = vidSpec?.videoInputCreditsPerSec?.[vidResVal];
    if (flatVideoInputRate != null) {
      const videoInputPoints = clips.reduce((sum, item) => sum + item.durationMs / 1000 * flatVideoInputRate, 0);
      const rawOutputPoints = (Number(vidSpec?.creditsPerSec?.[vidResVal]) || 0) * vidDurVal;
      return Math.max(1, Math.ceil(rawOutputPoints + videoInputPoints + imagePoints));
    }
    if (!clips.length || !base || !videoIn || vidSpec?.creditsPerSec?.[vidResVal] == null) return Math.ceil(vidOutputCost + imagePoints);
    const tokenPoints = (w, h, fps, sec) => (w * h * fps * sec) / 1024 * videoIn / 1e6 * 15;
    const inputPoints = clips.reduce((sum, m) => sum + tokenPoints(m.width, m.height, m.fps, m.durationMs / 1000), 0);
    const outputAtVideoInRate = vidSpec.creditsPerSec[vidResVal] * vidDurVal * (videoIn / base);
    return Math.max(1, Math.ceil(outputAtVideoInRate + inputPoints + imagePoints));
  }, [refVideoMetas, effectiveVideoRefImageNodes.length, vidSpec, vidResVal, vidDurVal, vidOutputCost]);
  // 参考音频总时长上限由模型目录下发；Seedance 为 15.2s，MiniMax H3 为 15s。
  // 提交前用节点自带的 duration_ms 先兜一层，给出可操作提示，别让用户白等一轮又回到初始态还不知道原因。
  // 只在能算出总时长且确实超限时拦（缺 duration_ms 的不误拦）。
  const REF_AUDIO_TOTAL_LIMIT_MS = Number(vidSpec?.referenceAudioMaxTotalMs) || Infinity;
  const refAudioTotalMs = useMemo(
    () => refVideoAudioNodes.reduce((sum, n) => sum + (Number(n.data?.duration_ms) || 0), 0),
    [refVideoAudioNodes]
  );
  const refAudioTooMany = refVideoAudioNodes.length > (Number(vidSpec?.maxReferenceAudios) || 3);
  const refAudioDurationInvalid = refVideoAudioNodes.some(node => {
    const ms = Number(node.data?.duration_ms) || 0;
    const min = Number(vidSpec?.referenceAudioMinMs) || 0;
    const max = Number(vidSpec?.referenceAudioMaxMs) || Infinity;
    return ms > 0 && (ms < min || ms > max);
  });
  const allowedAudioFormats = (vidSpec?.referenceAudioFormats || []).map(value => String(value).toLowerCase());
  const refAudioFormatInvalid = allowedAudioFormats.length > 0 && refVideoAudioNodes.some(node => {
    const explicit = String(node.data?.format || '').toLowerCase();
    const extension = String(node.data?.url || '').split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase();
    const format = explicit === 'mpeg' ? 'mp3' : (explicit || extension || '');
    return format && !allowedAudioFormats.includes(format);
  });
  const refAudioOverLimit = refVideoAudioNodes.length > 0
    && (refAudioTooMany || refAudioTotalMs > REF_AUDIO_TOTAL_LIMIT_MS || refAudioDurationInvalid || refAudioFormatInvalid);
  const refAudioNeedsVisual = refVideoAudioNodes.length > 0 && vidSpec?.requiresVisualForReferenceAudio && refImgNodes.length === 0 && refVideoNodes.length === 0;
  const maxReferenceImages = Number(vidSpec?.maxReferenceImages) || 9;
  const refImageTooMany = effectiveVideoRefImageNodes.length > maxReferenceImages;
  const maxReferenceFiles = Number(vidSpec?.maxReferenceFiles) || Infinity;
  const refMediaTooMany = effectiveVideoRefImageNodes.length + refVideoNodes.length + refVideoAudioNodes.length > maxReferenceFiles;

  // 选中图片节点时，找出连入的文本节点（作为提示词来源）
  const refTextNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'imageNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'textNode');
  }, [selectedNode, selectedId, edges, nodes]);
  const linkedText = refTextNodes.map(n => n.data.content).filter(Boolean).join('，');

  // 选中图片节点时，连入的图片节点（图生图参考）
  const refImageInputs = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'imageNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'imageNode' && n.data.url);
  }, [selectedNode, selectedId, edges, nodes]);

  // 选中视频节点时，连入的文本节点（作为提示词）
  const refVideoTextNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'videoNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'textNode');
  }, [selectedNode, selectedId, edges, nodes]);
  const linkedVideoText = refVideoTextNodes.map(n => n.data.content).filter(Boolean).join('，');

  // 选中音频节点时，文本连线提供台词；音频连线提供已注册 voice_id。
  const refAudioTextNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'audioNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'textNode');
  }, [selectedNode, selectedId, edges, nodes]);
  const linkedAudioText = refAudioTextNodes.map(n => n.data.content).filter(Boolean).join('，');
  const refAudioVoiceNodes = useMemo(() => {
    if (!selectedNode || selectedNode.type !== 'audioNode') return [];
    return edges
      .filter(e => e.target === selectedId)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n && n.type === 'audioNode');
  }, [selectedNode, selectedId, edges, nodes]);
  const audTextForGeneration = [linkedAudioText, audText.trim()].filter(Boolean).join('，');
  const audCost = audSpec && audTextForGeneration
    ? Math.max(1, Math.ceil((Array.from(audTextForGeneration).length / 10000) * (audSpec.creditsPer10kChars || 0)))
    : 0;

  // 拉取模型列表
  useEffect(() => {
    api.get('/canvas/models').then(r => setModels(r.data)).catch(() => {});
    api.get('/canvas/styles').then(r => setArtStyles(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchMe = () => api.get('/users/me').then(r => setMe(r.data)).catch(() => {});
    fetchMe();
    window.addEventListener('credits-updated', fetchMe);
    return () => window.removeEventListener('credits-updated', fetchMe);
  }, []);

  // 给节点改名（双击节点标题触发的事件）：本地立即改 + 持久化
  const renameNode = useCallback(async (nodeId, label) => {
    if (!canvasId || !nodeId) return;
    const name = String(label || '').trim();
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, label: name } } : n));
    const cur = rfRef.current?.getNode?.(nodeId);
    const data = { ...(cur?.data || {}), label: name };
    try { await api.patch(`/canvas/${canvasId}/nodes/${nodeId}`, { data }); } catch { /* 本地已改，忽略 */ }
  }, [canvasId, setNodes]);

  useEffect(() => {
    const handler = (e) => { if (e?.detail?.id) renameNode(e.detail.id, e.detail.label); };
    window.addEventListener('canvas-node-rename', handler);
    return () => window.removeEventListener('canvas-node-rename', handler);
  }, [renameNode]);

  useEffect(() => {
    const handler = (event) => {
      if (!event?.detail?.id) return;
      const node = rfRef.current?.getNode?.(event.detail.id);
      const history = Array.isArray(node?.data?.agent_history) ? node.data.agent_history.slice(-6) : [];
      setDirectorError('');
      setDirectorAgentHistory(history);
      setDirectorAgentReply(history[history.length - 1]?.reply || '');
      setDirectorNodeId(event.detail.id);
    };
    window.addEventListener('canvas-open-pose3d', handler);
    return () => window.removeEventListener('canvas-open-pose3d', handler);
  }, []);

  const patchDirectorNodeData = useCallback((nodeId, dataPatch) => {
    if (!canvasId || !nodeId || !dataPatch || typeof dataPatch !== 'object') return Promise.resolve();
    setNodes(nds => nds.map(node => node.id === nodeId
      ? { ...node, data: { ...node.data, ...dataPatch } }
      : node));

    const previous = directorNodeWriteQueueRef.current.get(nodeId) || Promise.resolve();
    const write = previous
      .catch(() => undefined)
      .then(() => api.patch(`/canvas/${canvasId}/nodes/${nodeId}`, { dataPatch }));
    directorNodeWriteQueueRef.current.set(nodeId, write);
    return write.finally(() => {
      if (directorNodeWriteQueueRef.current.get(nodeId) === write) {
        directorNodeWriteQueueRef.current.delete(nodeId);
      }
    });
  }, [canvasId, setNodes]);

  const persistDirectorProject = useCallback(async (nodeId, project, extraData = {}) => {
    if (!project) return;
    await patchDirectorNodeData(nodeId, { ...extraData, directorProject: project });
  }, [patchDirectorNodeData]);

  const sendDirectorPanorama = useCallback(async () => {
    if (!directorNodeId || !directorIframeRef.current?.contentWindow) return;
    const frameWindow = directorIframeRef.current.contentWindow;
    const currentEdges = rfRef.current?.getEdges?.() || [];
    const backgroundEdge = currentEdges.find(edge => edge.target === directorNodeId);
    const sourceNode = backgroundEdge ? rfRef.current?.getNode?.(backgroundEdge.source) : null;
    if (!backgroundEdge || sourceNode?.type !== 'imageNode' || !sourceNode.data?.url) return;

    try {
      const response = await api.post(`/canvas/${canvasId}/nodes/${directorNodeId}/pose-background`, {
        sourceNodeId: sourceNode.id,
      });
      frameWindow.postMessage({
        type: 'storyai:director-desk-panorama',
        payload: {
          imageUrl: response.data.dataUrl,
          fileName: response.data.fileName || sourceNode.data.label || '画布背景图',
          edgeId: backgroundEdge.id,
          sourceNodeId: sourceNode.id,
        },
      }, window.location.origin);
    } catch (error) {
      setDirectorError(error.response?.data?.error || '背景图载入失败');
    }
  }, [canvasId, directorNodeId]);

  const sendDirectorSession = useCallback(async () => {
    if (!directorNodeId || !directorIframeRef.current?.contentWindow) return;
    const frameWindow = directorIframeRef.current.contentWindow;
    const node = rfRef.current?.getNode?.(directorNodeId);
    if (!node) return;
    frameWindow.postMessage({
      type: 'storyai:director-desk-session',
      payload: { instanceId: directorNodeId, theme: isDark ? 'dark' : 'light', project: node.data?.directorProject || null },
    }, window.location.origin);
    await sendDirectorPanorama();
  }, [directorNodeId, isDark, sendDirectorPanorama]);

  const closeDirectorModal = useCallback(() => {
    setDirectorNodeId(null);
    setDirectorBusy(false);
    setDirectorClosing(false);
    setDirectorAgentInput('');
    setDirectorAgentLoading(false);
    setDirectorAgentReply('');
    setDirectorAgentHistory([]);
    setDirectorAgentHistoryOpen(false);
    setDirectorError('');
  }, []);

  const requestDirectorProject = useCallback(() => new Promise((resolve, reject) => {
    const frameWindow = directorIframeRef.current?.contentWindow;
    if (!frameWindow || directorExportResolverRef.current) {
      reject(new Error('导演台尚未准备好'));
      return;
    }
    directorExportSequenceRef.current += 1;
    const requestId = `director_export_${Date.now()}_${directorExportSequenceRef.current}`;
    const timer = window.setTimeout(() => {
      if (directorExportResolverRef.current?.requestId === requestId) directorExportResolverRef.current = null;
      reject(new Error('读取导演台场景超时'));
    }, 3000);
    directorExportResolverRef.current = { resolve, reject, timer, requestId };
    frameWindow.postMessage({
      type: 'storyai:director-desk-export-request',
      payload: { requestId },
    }, window.location.origin);
  }), []);

  const requestDirectorClose = useCallback(async () => {
    if (!directorNodeId || directorBusy || directorAgentLoading || directorClosing) return;
    if (!directorIframeRef.current?.contentWindow) return closeDirectorModal();
    setDirectorClosing(true);
    setDirectorError('');
    try {
      const project = await requestDirectorProject();
      await persistDirectorProject(directorNodeId, project, { agent_history: directorAgentHistory.slice(-6) });
      closeDirectorModal();
    } catch (error) {
      setDirectorError(error.response?.data?.error || error.message || '工程状态保存失败，请重试');
      setDirectorClosing(false);
    }
  }, [closeDirectorModal, directorAgentHistory, directorAgentLoading, directorBusy, directorClosing, directorNodeId, persistDirectorProject, requestDirectorProject]);

  const runDirectorPoseAgent = useCallback(async () => {
    const instruction = directorAgentInput.trim();
    if (!canvasId || !directorNodeId || !instruction || directorAgentLoading) return;
    setDirectorAgentLoading(true);
    setDirectorError('');
    try {
      const project = await requestDirectorProject();
      const response = await api.post(`/canvas/${canvasId}/nodes/${directorNodeId}/pose-agent`, {
        instruction,
        project,
        history: directorAgentHistory.slice(-6),
      });
      const nextProject = response.data.project;
      const applyRequestId = `director_apply_${Date.now()}`;
      directorIframeRef.current?.contentWindow?.postMessage({
        type: 'storyai:director-desk-apply-project',
        payload: { project: nextProject, requestId: applyRequestId },
      }, window.location.origin);
      await sendDirectorPanorama();
      const reply = response.data.reply || '已调整场面调度。';
      const nextHistory = [...directorAgentHistory, { instruction, reply, ts: new Date().toISOString() }].slice(-6);
      await persistDirectorProject(directorNodeId, nextProject, { agent_history: nextHistory });
      setDirectorAgentHistory(nextHistory);
      setDirectorAgentReply(reply);
      setDirectorAgentInput('');
    } catch (error) {
      setDirectorError(error.response?.data?.error || error.message || '场面调度失败');
    } finally {
      setDirectorAgentLoading(false);
    }
  }, [canvasId, directorAgentHistory, directorAgentInput, directorAgentLoading, directorNodeId, persistDirectorProject, requestDirectorProject, sendDirectorPanorama]);

  // dataURL 内容哈希（djb2）：同一张截图不管被重发多少次（重开弹窗重放/手滑点两次“发送到画布”）都只导入一次
  const hashDataUrl = (s) => { let h = 5381; for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; return `djb2_${h.toString(16)}_${s.length}`; };

  const importDirectorCaptures = useCallback(async (nodeId, payload) => {
    const captureItems = Array.isArray(payload?.captures) ? payload.captures : [];
    if (!captureItems.length) return;
    const poseNode = rfRef.current?.getNode?.(nodeId);
    if (!poseNode) return;
    let poseData = {
      ...poseNode.data,
      label: poseNode.data?.label || '3D导演台',
      ...(payload?.project ? { directorProject: payload.project } : {}),
    };
    const existingCaptureCount = poseData.captures?.length || 0;
    setDirectorBusy(true);
    setDirectorError('');
    try {
      for (let index = 0; index < captureItems.length; index += 1) {
        const item = captureItems[index];
        const match = /^data:(image\/(?:png|jpeg|webp));base64,(.+)$/s.exec(item?.dataUrl || '');
        if (!match) throw new Error('导演台返回了无效的截图数据');
        const contentHash = hashDataUrl(item.dataUrl);
        if ((poseData.captures || []).some(c => c.content_hash === contentHash)) continue; // 已导入过，跳过
        const upload = await api.post(`/canvas/${canvasId}/nodes/${nodeId}/pose-capture`, {
          base64: match[2], mimeType: match[1], meta: item.meta || null,
        });
        const created = await api.post(`/canvas/${canvasId}/nodes`, {
          type: 'image',
          x: poseNode.position.x + 330,
          y: poseNode.position.y + (existingCaptureCount + index) * 190,
          data: {
            label: String(item.fileName || `3D构图 ${index + 1}`).replace(/\.[^.]+$/, ''),
            url: upload.data.url,
            url_expires_at: upload.data.url_expires_at,
            storage: upload.data.storage,
            storage_key: upload.data.storage_key,
            status: 'done',
            source: 'pose3d',
            pose_meta: upload.data.meta || item.meta || null,
          },
        });
        const edge = await api.post(`/canvas/${canvasId}/edges`, { source: nodeId, target: created.data.id });
        setNodes(nds => [...nds, toFlowNode(created.data)]);
        setEdges(eds => addEdge({ source: nodeId, target: created.data.id, id: edge.data.id, ...edgeOptions }, eds));
        const importedCapture = {
          url: upload.data.url,
          url_expires_at: upload.data.url_expires_at,
          storage: upload.data.storage,
          storage_key: upload.data.storage_key,
          meta: upload.data.meta || item.meta || null,
          derived_node_id: created.data.id,
          content_hash: contentHash,
          created_at: new Date().toISOString(),
        };
        const nextPoseData = { ...poseData, captures: [...(poseData.captures || []), importedCapture] };
        poseData = nextPoseData;
        await patchDirectorNodeData(nodeId, {
          label: nextPoseData.label,
          captures: nextPoseData.captures,
          ...(payload?.project ? { directorProject: payload.project } : {}),
        });
      }
    } catch (error) {
      setDirectorError(error.response?.data?.error || error.message || '截图转存失败');
    } finally {
      setDirectorBusy(false);
    }
  }, [canvasId, patchDirectorNodeData, setEdges, setNodes]);

  useEffect(() => {
    if (!directorNodeId) return undefined;
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin || event.source !== directorIframeRef.current?.contentWindow) return;
      const { type, payload } = event.data || {};
      if (type === 'storyai:director-desk-ready') {
        void sendDirectorSession();
      } else if (type === 'storyai:director-desk-captures-sent') {
        void importDirectorCaptures(directorNodeId, payload);
      } else if (type === 'storyai:director-desk-export-result') {
        if (directorExportResolverRef.current?.requestId === payload?.requestId) {
          const pending = directorExportResolverRef.current;
          directorExportResolverRef.current = null;
          window.clearTimeout(pending.timer);
          pending.resolve(payload?.project);
        }
      } else if (type === 'storyai:director-desk-error') {
        const message = payload?.message || '导演台工程处理失败';
        if (directorExportResolverRef.current?.requestId === payload?.requestId) {
          const pending = directorExportResolverRef.current;
          directorExportResolverRef.current = null;
          window.clearTimeout(pending.timer);
          pending.reject(new Error(message));
        }
        setDirectorError(message);
      } else if (type === 'storyai:director-desk-close') {
        requestDirectorClose();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [directorNodeId, importDirectorCaptures, requestDirectorClose, sendDirectorSession]);

  useEffect(() => () => {
    if (directorExportResolverRef.current) {
      window.clearTimeout(directorExportResolverRef.current.timer);
      directorExportResolverRef.current.reject(new Error('导演台已关闭'));
      directorExportResolverRef.current = null;
    }
  }, []);

  const loadCanvas = useCallback(async () => {
    if (!canvasId) return;
    setLoading(true);
    try {
      const r = await api.get(`/canvas/${canvasId}`);
      setCanvasName(r.data.canvas.name);
      setNodes(r.data.nodes.map(toFlowNode));
      setEdges(r.data.edges.map(toFlowEdge));
    } catch {
      navigate('/canvas');
    } finally {
      setLoading(false);
    }
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 增量合并（后台生成时用）：只更新变化的节点 + 追加新节点，不动已有的，避免整张画布闪烁重载，
  // 同时实现"节点一个个长出来"的渐进式构建。
  const mergeCanvasFromServer = useCallback(async () => {
    if (!canvasId) return;
    try {
      const r = await api.get(`/canvas/${canvasId}`);
      setCanvasName(r.data.canvas.name);
      const incoming = (r.data.nodes || []).map(toFlowNode);
      // 预签名URL每次GET都会重签，url/url_expires_at 会变但其实图没变 —— 比较时忽略它们，否则每轮都"变"导致图片反复重载
      const strip = (d) => { const { url, url_expires_at, ...rest } = d || {}; return rest; };
      setNodes(prev => {
        const prevById = new Map(prev.map(n => [n.id, n]));
        return incoming.map(nn => {
          const old = prevById.get(nn.id);
          if (!old) return nn; // 新节点：追加
          const hadUrl = Boolean(old.data?.url);
          const hasUrl = Boolean(nn.data?.url);
          const stableSame = JSON.stringify(strip(old.data)) === JSON.stringify(strip(nn.data));
          if (stableSame && hadUrl === hasUrl) return old; // 实质没变 → 原样保留(不重渲染、不闪)
          const nextData = { ...nn.data };
          if (hadUrl && hasUrl) { nextData.url = old.data.url; nextData.url_expires_at = old.data.url_expires_at; } // 已有图沿用旧url，避免图片闪一下
          return { ...old, data: nextData };
        });
      });
      setEdges(prev => {
        const incomingEdges = (r.data.edges || []).map(toFlowEdge);
        const incomingIds = new Set(incomingEdges.map(e => e.id));
        const prevIds = new Set(prev.map(e => e.id));
        const kept = prev.filter(e => incomingIds.has(e.id));
        const added = incomingEdges.filter(e => !prevIds.has(e.id));
        return [...kept, ...added];
      });
    } catch { /* 瞬时失败忽略，下个周期再合 */ }
  }, [canvasId, setEdges, setNodes]);

  // 智能生产轮询：只要画布上还有"生成中/处理中"的节点就持续增量刷新。
  // 长批次（如 80 张资产图串行 20-40 分钟）远超单次 HTTP 超时(3分钟)，请求断了后端仍在生成，
  // 全靠它把陆续完成的图接回前端（治"永远显示生成中"）。空闲自动停，硬上限 40 分钟兜底。
  const startProductionPoll = useCallback(() => {
    if (productionPollRef.current) clearInterval(productionPollRef.current);
    let ticks = 0;
    let idle = 0;
    productionPollRef.current = setInterval(() => {
      mergeCanvasFromServer();
      agentLoadRef.current?.(); // 重新拉对话：取回建图后"素材怎么补"等弹选择消息
      setPlanRefreshVersion(version => version + 1);
      ticks += 1;
      const busy = (rfRef.current?.getNodes?.() || []).some(n => ['generating', 'processing'].includes(n.data?.status));
      idle = busy ? 0 : idle + 1;
      // 起步至少轮 30s（后端可能还没来得及把节点标成 generating）；连续 3 个空周期即停；1200 tick(40分钟)硬停
      if ((ticks >= 15 && idle >= 3) || ticks >= 1200) { clearInterval(productionPollRef.current); productionPollRef.current = null; }
    }, 2000);
  }, [mergeCanvasFromServer]);

  // 把选中的普通图片节点"设为素材"，让 Agent 能识别并连回分镜
  const setNodeAsAsset = useCallback(async (nodeId, name, type) => {
    if (!canvasId || !nodeId || !String(name || '').trim()) return;
    try {
      const r = await api.patch(`/canvas/${canvasId}/agent/nodes/${nodeId}/set-asset`, { name: String(name).trim(), type });
      await loadCanvas();
      setPlanRefreshVersion(version => version + 1);
      setAgentErr('');
      setAgentNotice(`已把「${r.data?.name || name}」设为素材${r.data?.linked ? `，自动连回 ${r.data.linked} 个分镜` : '（暂无引用它的分镜）'}。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '设为素材失败');
    }
  }, [canvasId, loadCanvas]);

  // 画布图片卡上"设为素材"(钉子)图标触发
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail;
      if (!d?.id) return;
      if (!d.name) { setAgentErr('请先给这张图改个名（如 叶长歌），再设为素材'); return; }
      setNodeAsAsset(d.id, d.name, d.type);
    };
    window.addEventListener('canvas-node-set-asset', handler);
    return () => window.removeEventListener('canvas-node-set-asset', handler);
  }, [setNodeAsAsset]);

  // 真人检测：只更新这一个节点的 data，不整画布重载（避免打断用户当前操作）
  const patchNodeLiveness = useCallback((nodeId, patch) => {
    setNodes(nds => nds.map(n => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
  }, [setNodes]);

  // 提交前按火山素材规格自检，别等接口打回来才报错（规格同 LibTV「待校验图像要求」提示）
  const checkImageSpec = (url) => new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      if (w < 300 || h < 300 || w > 6000 || h > 6000) {
        return resolve(`图片边长需在 300~6000px 之间（当前 ${w}×${h}）`);
      }
      const ratio = w / h;
      if (ratio <= 0.4 || ratio >= 2.5) {
        return resolve(`图片宽高比需在 0.4~2.5 之间（当前 ${ratio.toFixed(2)}）`);
      }
      resolve(null);
    };
    img.onerror = () => resolve(null); // 量不到就不拦，交给后端/火山判
    img.src = url;
  });

  const verifyNodeLiveness = useCallback(async (nodeId) => {
    if (!canvasId || !nodeId) return;
    const node = rfRef.current?.getNodes?.().find(n => n.id === nodeId);
    if (node?.data?.url) {
      const specErr = await checkImageSpec(node.data.url);
      if (specErr) { setAgentErr(`${specErr}——不满足 Seedance 合规校验的图像要求`); return; }
    }
    patchNodeLiveness(nodeId, { liveness_status: 'processing' });
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes/${nodeId}/verify-liveness`);
      const status = r.data?.liveness_status || 'processing';
      patchNodeLiveness(nodeId, { liveness_status: status, volc_asset_id: r.data?.volc_asset_id });
      if (status === 'active') setAgentNotice('已通过 Seedance 2.0 合规校验，这张图可以放心用于视频生成了。');
      else if (status === 'failed') setAgentErr('未通过内容审核——这张图可能被判定为真人肖像，建议换一张 AI 生成的角色图。');
      // 火山未在超时内给终态 → 继续轮询（CreateAsset 异步且不承诺 SLA）
      if (status === 'processing') {
        let tries = 0;
        const timer = setInterval(async () => {
          tries += 1;
          try {
            const s = await api.get(`/canvas/${canvasId}/nodes/${nodeId}/liveness-status`);
            const cur = s.data?.liveness_status;
            if (cur === 'active' || cur === 'failed' || tries >= 20) {
              clearInterval(timer);
              patchNodeLiveness(nodeId, { liveness_status: cur || 'processing' });
              if (cur === 'active') setAgentNotice('已通过 Seedance 2.0 合规校验，这张图可以放心用于视频生成了。');
              if (cur === 'failed') setAgentErr('未通过内容审核——这张图可能被判定为真人肖像。');
            }
          } catch { clearInterval(timer); }
        }, 3000);
      }
    } catch (e) {
      patchNodeLiveness(nodeId, { liveness_status: null });
      setAgentErr(e.response?.data?.error || '真人检测失败');
    }
  }, [canvasId, patchNodeLiveness]);

  // 右键节点 → 菜单（阻止浏览器默认菜单）
  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    setSelectedId(node.id);
    setAddMenuOpen(false);
    setNodeMenu({ x: e.clientX, y: e.clientY, node });
  }, []);

  // ESC 关菜单
  useEffect(() => {
    if (!nodeMenu) return;
    const onKey = (e) => { if (e.key === 'Escape') setNodeMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nodeMenu]);

  const copyNodeImage = useCallback(async (node) => {
    const url = node?.data?.url;
    if (!url) { setAgentErr('这个节点还没有图片'); return; }
    try {
      const blob = await (await fetch(url)).blob();
      await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
      setAgentNotice('图片已复制到剪贴板');
    } catch {
      // 浏览器不支持写图片剪贴板时退回复制链接，别让用户点了没反应
      try { await navigator.clipboard.writeText(url); setAgentNotice('已复制图片链接'); }
      catch { setAgentErr('复制失败'); }
    }
  }, []);

  const duplicateNode = useCallback(async (node) => {
    if (!canvasId || !node) return;
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes`, {
        type: node.type.replace('Node', ''),
        x: (node.position?.x || 0) + 40,
        y: (node.position?.y || 0) + 40,
        data: { ...node.data, liveness_status: undefined, volc_asset_id: undefined }, // 副本重新检测
      });
      setNodes(nds => [...nds, toFlowNode(r.data)]);
      setSelectedId(r.data.id);
      setAgentNotice('已创建副本');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '创建副本失败');
    }
  }, [canvasId, setNodes]);

  // 资产库 → 画布：把库里的资产文件加成节点（图片→imageNode，声线→audioNode），可直接引用/连线
  const useAssetOnCanvas = useCallback(async (asset, file) => {
    if (!canvasId || !file?.url) return;
    let x = 200, y = 200;
    if (rfRef.current) {
      const { x: vx, y: vy, zoom } = rfRef.current.getViewport();
      x = (-vx + (window.innerWidth - 220) / 2 - 100) / zoom;
      y = (-vy + window.innerHeight / 2 - 80) / zoom;
    }
    const isVoice = asset.type === 'voice';
    const fd = file.data || {};
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes`, {
        type: isVoice ? 'audio' : 'image', x, y,
        data: isVoice
          ? {
              url: file.url,
              status: 'done',
              label: asset.name,
              source: fd.source || 'voice_clone',
              voice_id: fd.voice_id || asset.data?.voice_id || null,
              voice_kind: fd.voice_kind || asset.data?.voice_kind || null,
              voice_model: fd.voice_model || null,
              transcript: fd.transcript || null,
              duration_ms: fd.duration_ms || null,
              format: fd.format || null,
              storage_key: file.storage_key || null,
              from_asset_id: asset.id,
              from_asset_file_id: file.id,
            }
          : {
              url: file.url,
              status: 'done',
              label: asset.name,
              from_asset_id: asset.id,
              from_asset_file_id: file.id,
            },
      });
      setNodes(nds => [...nds, toFlowNode(r.data)]);
      setSelectedId(r.data.id);
      setAssetPanelOpen(false);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '引用资产失败');
    }
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 画布 → 资产库：把选中节点沉淀成账号级资产（跨画布跨剧复用）
  // 图片 → character/scene/prop/style；音频（已锁声线）→ voice
  const [depositBusy, setDepositBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const depositToAssetLibrary = useCallback(async (node, type) => {
    if (!node?.data?.url) return;
    const isAudio = node.type === 'audioNode';
    const assetType = type || (isAudio ? 'voice' : 'character');
    setDepositBusy(true);
    try {
      const name = (node.data.label || '').trim() || (isAudio ? '未命名声线' : '未命名资产');
      const asset = (await api.post('/assets', {
        type: assetType,
        name,
        description: isAudio ? (node.data.transcript || '') : (node.data.prompt || ''),
        data: {
          origin: 'canvas', canvas_id: canvasId, node_id: node.id,
          ...(isAudio ? { voice_id: node.data.voice_id || null, voice_kind: node.data.voice_kind || null } : {}),
        },
      })).data;
      const file = (await api.post(`/assets/${asset.id}/files`, {
        url: node.data.url,
        kind: 'other',
        storage_key: node.data.storage_key || null,
        mime: isAudio ? (node.data.mime || null) : null,
        data: isAudio
          ? {
              voice_id: node.data.voice_id || null,
              voice_kind: node.data.voice_kind || null,
              voice_model: node.data.voice_model || null,
              source: node.data.source || null,
              transcript: node.data.transcript || null,
              duration_ms: node.data.duration_ms || null,
              format: node.data.format || null,
            }
          : { model: node.data.model, prompt: node.data.prompt },
      })).data;
      // 把溯源写回节点：否则节点不知道自己对应库里哪个资产，
      // 在资产面板点了「入库」画布上也不会亮盾（两条检测路径就此接上）
      if (file?.id) {
        const link = { from_asset_id: asset.id, from_asset_file_id: file.id };
        try {
          await api.patch(`/canvas/${canvasId}/nodes/${node.id}`, { dataPatch: link });
          setNodes(nds => nds.map(n => (n.id === node.id ? { ...n, data: { ...n.data, ...link } } : n)));
        } catch { /* 回写失败不影响入库本身 */ }
      }
      setAgentErr('');
      setAgentNotice(
        isAudio
          ? `已把「${name}」声线存入资产库${node.data.voice_id ? '（含固定声线，可跨画布复用）' : ''}。`
          : `已把「${name}」存入资产库（${assetType}），可在资产面板点「入库」做合规校验，之后画布上会亮蓝盾。`
      );
    } catch (e) {
      setAgentErr(e.response?.data?.error || '存入资产库失败');
    } finally {
      setDepositBusy(false);
    }
  }, [canvasId, setNodes]);

  // 从视频节点提取音轨 → 新建音频节点（供 Seedance 2.0 参考音频复用，保持跨片段声线一致）
  const extractAudioFromVideoNode = useCallback(async (node) => {
    if (!canvasId || !node?.data?.url) return;
    setExtractBusy(true);
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes/${node.id}/extract-audio`);
      setNodes(nds => [...nds, toFlowNode(r.data)]);
      setSelectedId(r.data.id);
      setAgentErr('');
      setAgentNotice('已提取声音，生成音频节点。把它连到其它 Seedance 2.0 视频节点（同时配参考图）即可保持角色声线一致。');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '提取声音失败');
    } finally {
      setExtractBusy(false);
    }
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载画布
  useEffect(() => {
    loadCanvas();
  }, [loadCanvas]);

  const handleAgentActionResult = useCallback(async (actionType, result) => {
    await loadCanvas();
    window.dispatchEvent(new Event('credits-updated'));
    if (['start_production', 'submit_videos', 'generate_assets', 'generate_keyframes'].includes(actionType)) {
      // 后台陆续建图/生成，前端独立增量轮询，让节点和图陆续出现（不依赖 agentExecuting，治"开拍后画布空"）
      if (actionType === 'start_production' || actionType === 'submit_videos') {
        setAgentNotice(actionType === 'start_production'
          ? '正在建图，节点会陆续出现…建好后在右侧素材清单挨个决定：勾选自动补齐 / 📎上传 / 跳过；弄好说“生成视频”。'
          : '视频陆续生成中…');
        setTimeout(() => { try { rfRef.current?.fitView?.({ padding: 0.16, duration: 320 }); } catch {} }, 1500);
      }
      startProductionPoll();
    }
    if (actionType === 'create_storyboard') {
      setAgentAnalysis(result?.plan?.analysis || null);
      setAgentNotice(`已确认建图：创建 ${result?.nodes?.length || 0} 个节点、${result?.edges?.length || 0} 条连线。`);
      setPlanRefreshVersion(version => version + 1);
      setTimeout(() => {
        try { rfRef.current?.fitView?.({ padding: 0.16, duration: 320 }); } catch {}
      }, 80);
      return;
    }
    if (actionType === 'generate_assets') setAgentNotice(`资产参考完成 ${result?.generated?.length || 0} 个。`);
    if (actionType === 'generate_keyframes') setAgentNotice(`首帧完成 ${result?.generated?.length || 0} 个。`);
    if (actionType === 'submit_videos') setAgentNotice(`视频已提交 ${result?.submitted?.length || 0} 个，生成完成后等待验收。`);
    setPlanRefreshVersion(version => version + 1);
  }, [loadCanvas, startProductionPoll]);

  const canvasAgent = useCanvasAgent(canvasId, handleAgentActionResult);
  agentLoadRef.current = canvasAgent.load; // 每次渲染更新引用，轮询里用它重新拉对话

  // 轮询生成中的视频节点
  useEffect(() => {
    if (!canvasId) return;
    const processing = nodes.filter(n => n.type === 'videoNode' && n.data?.status === 'processing' && n.data?.taskId);
    if (!processing.length) return;
    const intervalMs = Math.max(3000, ...processing.map(node => Number(models.video.find(model => model.key === node.data?.model)?.pollIntervalMs) || 3000));
    const tid = setInterval(async () => {
      for (const node of processing) {
        try {
          const r = await api.post(`/canvas/${canvasId}/nodes/${node.id}/poll-video`, { taskId: node.data.taskId, provider: node.data.provider });
          if (r.data.status === 'succeeded' || r.data.status === 'failed') {
            // 必须把 storage/storage_key 一并 merge 进来：这里只更新内存节点、不重拉画布，
            // 漏了 storage 就等于前端永远认为"还没归档"（曾因此误拦参考视频，库里其实早归档好了）。
            const archivePatch = {};
            ['storage', 'storage_key', 'archived_at'].forEach(key => {
              if (r.data[key] != null) archivePatch[key] = r.data[key];
            });
            // archive_error 要原样跟随（含 null）：它驱动那个小红叹号，
            // 用"非空才 merge"会让一次失败后即使搬成功了叹号也永久不消。
            if ('archive_error' in r.data) archivePatch.archive_error = r.data.archive_error;
            setNodes(nds => nds.map(n => n.id === node.id
              ? { ...n, data: { ...n.data, status: r.data.status, ...(r.data.videoUrl ? { url: r.data.videoUrl } : {}), ...archivePatch } }
              : n
            ));
            if (r.data.status === 'failed') window.dispatchEvent(new Event('credits-updated'));
          }
        } catch {}
      }
    }, intervalMs);
    return () => clearInterval(tid);
  }, [nodes, canvasId, models.video]); // eslint-disable-line react-hooks/exhaustive-deps

  // 仅"后台一条龙(开拍)执行中"才定时全量拉取——那是后台批量生成、前端无从增量感知。
  // 单个换一张/手动生成走各自的局部节点更新，不触发整张画布与对话的重拉。
  const agentExecuting = (canvasAgent.actions || []).some(a => a.status === 'executing');
  useEffect(() => {
    if (!canvasId || !agentExecuting) return;
    const tid = setInterval(() => {
      mergeCanvasFromServer(); // 增量合并，不再整张重拉，避免反复闪烁
      canvasAgent.load?.();
      setPlanRefreshVersion(version => version + 1);
    }, 4000);
    return () => clearInterval(tid);
  }, [canvasId, agentExecuting, mergeCanvasFromServer]); // eslint-disable-line react-hooks/exhaustive-deps

  // 面板清单轻量自校正：有素材在生成时，定时只刷新缺口预检(不重拉画布/对话)，
  // 让"生成中"自动翻成 ✓ 或 失败，不必整页刷新。
  const hasGeneratingGap = (productionPlan?.gaps || []).some(g => g.status === 'generating') || generatingGapIds.size > 0;
  useEffect(() => {
    if (!canvasId || !hasGeneratingGap) return;
    const tid = setInterval(() => setPlanRefreshVersion(v => v + 1), 4000);
    return () => clearInterval(tid);
  }, [canvasId, hasGeneratingGap]);

  // 选中节点时同步面板
  useEffect(() => {
    if (!selectedNode) return;
    setGenErr('');
    if (selectedNode.type === 'imageNode') {
      setImgPrompt(selectedNode.data.prompt_editor_text ?? selectedNode.data.prompt ?? '');
      setImgViewIdx(0);
      setImgTab('generate');
      // 已生成过的节点读它自己存下的配置；没配置的新节点保持当前面板值（= 上次用的），不回退写死默认
      if (selectedNode.data.model) setImgModel(selectedNode.data.model);
    } else if (selectedNode.type === 'videoNode') {
      setVidPrompt(selectedNode.data.prompt_editor_text ?? selectedNode.data.prompt ?? '');
      if (selectedNode.data.aspect_ratio) setVidRatio(selectedNode.data.aspect_ratio);
      if (selectedNode.data.duration)     setVidDur(selectedNode.data.duration);
      if (selectedNode.data.model)        setVidModel(selectedNode.data.model);
      if (selectedNode.data.resolution)   setVidRes(selectedNode.data.resolution);
    } else if (selectedNode.type === 'textNode') {
      setTxtContent(selectedNode.data.content || '');
    } else if (selectedNode.type === 'audioNode') {
      setAudText(selectedNode.data.transcript || '');
      setAudModel(selectedNode.data.model || 'qwen-audio-tts-flash');
      setAudVoice(selectedNode.data.system_voice || 'longanhuan_v3.6');
      setVoiceRightsConfirmed(Boolean(selectedNode.data.rights_confirmed));
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 添加节点（居中于当前视口）
  const addNode = useCallback(async (type) => {
    if (!canvasId) return;
    setAddMenuOpen(false);
    let x = 200, y = 200;
    if (rfRef.current) {
      const { x: vx, y: vy, zoom } = rfRef.current.getViewport();
      x = (-vx + (window.innerWidth - 220) / 2 - 100) / zoom;
      y = (-vy + window.innerHeight / 2 - 80) / zoom;
    }
    const r = await api.post(`/canvas/${canvasId}/nodes`, { type, x, y, data: {} });
    setNodes(nds => [...nds, toFlowNode(r.data)]);
    setSelectedId(r.data.id);
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 连线（文本→图片/音频/视频；音频→音频/视频；图片→图片/视频/3D导演台；视频→视频=参考视频）
  const onConnect = useCallback((params) => {
    if (!canvasId || !rfRef.current) return;
    const cur = rfRef.current.getNodes();
    const src = cur.find(n => n.id === params.source);
    const tgt = cur.find(n => n.id === params.target);
    const ok =
      (src?.type === 'textNode'  && tgt?.type === 'imageNode') ||
      (src?.type === 'textNode'  && tgt?.type === 'textNode') ||
      (src?.type === 'textNode'  && tgt?.type === 'audioNode') ||
      (src?.type === 'textNode'  && tgt?.type === 'videoNode') ||
      (src?.type === 'imageNode' && tgt?.type === 'imageNode') ||
      (src?.type === 'imageNode' && tgt?.type === 'videoNode') ||
      (src?.type === 'imageNode' && tgt?.type === 'pose3dNode') ||
      (src?.type === 'audioNode' && tgt?.type === 'audioNode') ||
      (src?.type === 'audioNode' && tgt?.type === 'videoNode') ||
      (src?.type === 'videoNode' && tgt?.type === 'videoNode');
    if (ok && src.id === tgt.id) return;
    if (!ok) return;
    // 同类连线（视频→视频/图片→图片/文本→文本）能绕回自己：A→B→A 会画出怪线。
    // 自环上面已挡，这里挡多跳——从 target 出发若能走回 source，加这条边就成环。后端有同样的递归校验兜底。
    if (src.type === tgt.type) {
      const outgoing = new Map();
      rfRef.current.getEdges().forEach(e => {
        if (!outgoing.has(e.source)) outgoing.set(e.source, []);
        outgoing.get(e.source).push(e.target);
      });
      const seen = new Set([tgt.id]);
      const queue = [tgt.id];
      while (queue.length) {
        const id = queue.shift();
        if (id === src.id) { setGenErr('这条连线会让节点绕回自己（形成环路），请换个方向连'); return; }
        (outgoing.get(id) || []).forEach(next => { if (!seen.has(next)) { seen.add(next); queue.push(next); } });
      }
    }
    api.post(`/canvas/${canvasId}/edges`, { source: params.source, target: params.target })
      .then(r => {
        setEdges(eds => addEdge({ ...params, id: r.data.id, ...edgeOptions }, eds));
        if (r.data.target_data) {
          setNodes(current => current.map(node => node.id === params.target
            ? { ...node, data: { ...node.data, ...r.data.target_data } }
            : node));
        }
        if (src?.type === 'audioNode' && tgt?.type === 'videoNode') setVidAudio(true);
      })
      .catch(error => setGenErr(error.response?.data?.error || '节点连接失败'));
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 节点拖拽结束 → 保存位置
  const onNodeDragStop = useCallback((_, node) => {
    if (!canvasId) return;
    api.patch(`/canvas/${canvasId}/nodes/${node.id}`, { x: node.position.x, y: node.position.y }).catch(() => {});
  }, [canvasId]);

  const onNodesDelete = useCallback((deleted) => {
    if (!canvasId) return;
    deleted.forEach(n => {
      api.delete(`/canvas/${canvasId}/nodes/${n.id}`).catch(() => {});
      if (n.id === selectedId) setSelectedId(null);
    });
  }, [canvasId, selectedId]);

  const onEdgesDelete = useCallback((deleted) => {
    if (!canvasId) return;
    deleted.forEach(e => api.delete(`/canvas/${canvasId}/edges/${e.id}`).then(response => {
      if (!response.data?.targetNodeId || !response.data?.target_data) return;
      setNodes(current => current.map(node => node.id === response.data.targetNodeId
        ? { ...node, data: { ...node.data, ...response.data.target_data } }
        : node));
    }).catch(() => {}));
  }, [canvasId, setNodes]);

  // 剪刀删除单条连线
  const cutEdge = useCallback((edgeId) => {
    if (!canvasId) return;
    setEdges(eds => eds.filter(e => e.id !== edgeId));
    api.delete(`/canvas/${canvasId}/edges/${edgeId}`).then(response => {
      if (!response.data?.targetNodeId || !response.data?.target_data) return;
      setNodes(current => current.map(node => node.id === response.data.targetNodeId
        ? { ...node, data: { ...node.data, ...response.data.target_data } }
        : node));
    }).catch(() => {});
  }, [canvasId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 给每条连线注入剪刀回调
  const edgesWithCut = useMemo(
    () => edges.map(e => ({ ...e, data: { ...e.data, onCut: cutEdge } })),
    [edges, cutEdge]
  );

  const runCanvasAgentStoryboard = async () => {
    const text = agentPrompt.trim();
    if (!canvasId || !text) return;
    setAgentLoading(true);
    setAgentErr('');
    setAgentNotice('');
    try {
      const viewport = rfRef.current?.getViewport?.();
      const r = await api.post(`/canvas/${canvasId}/agent/storyboard`, {
        text,
        title: canvasName || '剧本拆镜',
        x: viewport ? (-viewport.x + 120) / viewport.zoom : 80,
        y: viewport ? (-viewport.y + 120) / viewport.zoom : 80,
        mode: 'auto',
      });
      const newNodes = (r.data.nodes || []).map(toFlowNode);
      const newEdges = (r.data.edges || []).map(toFlowEdge);
      setNodes(nds => [...nds, ...newNodes]);
      setEdges(eds => [...eds, ...newEdges]);
      if (newNodes[0]) setSelectedId(newNodes[0].id);
      setAgentPrompt('');
      const analysis = r.data.plan?.analysis || null;
      const sourceLabel = analysis?.analysis_source === 'llm' || analysis?.source === 'llm'
        ? 'LLM 拆解'
        : '规则兜底';
      setAgentAnalysis(analysis);
      setAgentNotice(`${sourceLabel}：已创建 ${newNodes.length} 个节点、${newEdges.length} 条连线。`);
      setPlanRefreshVersion(version => version + 1);
      setTimeout(() => {
        try { rfRef.current?.fitView?.({ padding: 0.18, duration: 300 }); } catch {}
      }, 50);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '拆镜建图失败');
    } finally {
      setAgentLoading(false);
    }
  };

  const confirmAgentNodes = async () => {
    if (!canvasId) return;
    const targets = selectedNode
      ? [selectedNode]
      : nodes.filter(n => ['asset', 'shot_task'].includes(n.data?.agent_role) && ['needs_confirm', 'missing'].includes(n.data?.status));
    if (!targets.length) {
      setAgentNotice('没有需要确认的节点。');
      setAgentErr('');
      return;
    }
    setConfirming(true);
    setAgentErr('');
    try {
      await Promise.all(targets.map(n => api.patch(`/canvas/${canvasId}/agent/nodes/${n.id}/status`, {
        status: 'accepted',
        qc_note: selectedNode ? '人工确认该节点' : '人工批量确认资产与分镜',
      })));
      const ids = new Set(targets.map(n => n.id));
      setNodes(nds => nds.map(n => ids.has(n.id)
        ? { ...n, data: { ...n.data, status: 'accepted', qc_note: selectedNode ? '人工确认该节点' : '人工批量确认资产与分镜' } }
        : n
      ));
      setAgentNotice(selectedNode ? '已确认当前节点。' : `已确认 ${targets.length} 个资产/分镜节点。`);
      setPlanRefreshVersion(version => version + 1);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '确认失败');
    } finally {
      setConfirming(false);
    }
  };

  const generateAgentAssets = async (nodeIds) => {
    if (!canvasId) return;
    setAssetRefLoading(true);
    if (Array.isArray(nodeIds) && nodeIds.length) {
      setNodes(nds => nds.map(n => nodeIds.includes(n.id) ? { ...n, data: { ...n.data, status: 'generating' } } : n));
    }
    setAgentErr('');
    setAgentNotice('');
    startProductionPoll(); // 大批次会超过单次请求超时：请求断了后端仍在生成，靠轮询把完成的图接回来
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/generate-assets`, {
        model: imgModelVal || 'seedream',
        size: imgSize,
        limit: 20,
        styleKey: agentStyle || undefined, // 套用面板选的题材画风
        ...(Array.isArray(nodeIds) && nodeIds.length ? { nodeIds } : {}),
      }, { timeout: 180000 });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { generated = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`资产参考完成 ${generated.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '部分资产参考图生成失败');
    } catch (e) {
      if (e.code === 'ECONNABORTED') {
        // 超时≠失败：后端在逐张串行生成（成功才扣点），轮询会把陆续完成的图接回画布
        setAgentNotice('本批较大，请求已超时但生成仍在后台继续，图片会陆续出现，不用重复点击。');
      } else {
        setAgentErr(e.response?.data?.error || '批量生成资产参考图失败');
      }
      await loadCanvas().catch(() => {}); // 把节点状态收敛到服务端真相，别停在本地乐观的"生成中"
    } finally {
      setAssetRefLoading(false);
    }
  };

  const updateAssetQc = async (qcStatus, note, statusOverride) => {
    if (!canvasId || !selectedNode || selectedNode.data?.agent_role !== 'asset') return;
    setAgentErr('');
    try {
      const status = statusOverride || (['usable', 'locked', 'needs_regen'].includes(qcStatus) ? 'accepted' : selectedNode.data?.status);
      await api.patch(`/canvas/${canvasId}/agent/nodes/${selectedNode.id}/status`, {
        status,
        qc_status: qcStatus,
        qc_note: note,
      });
      setNodes(nds => nds.map(n => n.id === selectedNode.id
        ? { ...n, data: { ...n.data, status, qc_status: qcStatus, qc_note: note } }
        : n
      ));
      setAgentNotice(`已更新资产质检：${note}`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '资产质检更新失败');
    }
  };

  const regenerateSelectedAsset = async () => {
    if (!canvasId || !selectedNode || selectedNode.data?.agent_role !== 'asset') return;
    setAssetRefLoading(true);
    setAgentErr('');
    setAgentNotice('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/generate-assets`, {
        model: imgModelVal || 'seedream',
        size: imgSize,
        limit: 1,
        nodeIds: [selectedNode.id],
        regenerate: true,
        generateAudio: supportsGeneratedAudio && vidAudio,
      });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { generated = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`资产重生完成 ${generated.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '资产重生失败');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '资产重生失败');
    } finally {
      setAssetRefLoading(false);
    }
  };

  const updateKeyframeQc = async (qcStatus, note, statusOverride) => {
    if (!canvasId || !selectedNode || selectedNode.data?.agent_role !== 'keyframe') return;
    setAgentErr('');
    try {
      const status = statusOverride || (['usable', 'locked', 'needs_regen'].includes(qcStatus) ? 'done' : selectedNode.data?.status);
      await api.patch(`/canvas/${canvasId}/agent/nodes/${selectedNode.id}/status`, {
        status,
        qc_status: qcStatus,
        qc_note: note,
      });
      setNodes(nds => nds.map(n => n.id === selectedNode.id
        ? { ...n, data: { ...n.data, status, qc_status: qcStatus, qc_note: note } }
        : n
      ));
      setAgentNotice(`已更新首帧质检：${note}`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '首帧质检更新失败');
    }
  };

  const regenerateSelectedKeyframe = async () => {
    if (!canvasId || !selectedNode || selectedNode.data?.agent_role !== 'keyframe') return;
    setKeyframeLoading(true);
    setAgentErr('');
    setAgentNotice('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/generate-keyframes`, {
        model: imgModelVal || 'seedream',
        size: imgSize,
        limit: 1,
        nodeIds: [selectedNode.id],
        regenerate: true,
      });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { generated = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`首帧重生完成 ${generated.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '首帧重生失败');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '首帧重生失败');
    } finally {
      setKeyframeLoading(false);
    }
  };

  const updateVideoQc = async (qcStatus, note, statusOverride) => {
    if (!canvasId || !selectedNode || !['clip', 'video_clip'].includes(selectedNode.data?.agent_role)) return;
    setAgentErr('');
    try {
      const status = statusOverride || (['usable', 'locked'].includes(qcStatus) && selectedNode.data?.url ? 'succeeded' : selectedNode.data?.status);
      await api.patch(`/canvas/${canvasId}/agent/nodes/${selectedNode.id}/status`, {
        status,
        qc_status: qcStatus,
        qc_note: note,
      });
      setNodes(nds => nds.map(n => n.id === selectedNode.id
        ? { ...n, data: { ...n.data, status, qc_status: qcStatus, qc_note: note } }
        : n
      ));
      setAgentNotice(`已更新视频质检：${note}`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '视频质检更新失败');
    }
  };

  const refreshProductionPlan = useCallback(async ({ silent = false } = {}) => {
    if (!canvasId) return;
    setProductionPlanLoading(true);
    if (!silent) setAgentErr('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/production-plan`, {
        imageModel: imgModelVal || 'seedream',
        videoModel: vidModelVal || 'seedance',
        resolution: vidResVal,
      });
      setProductionPlan(r.data);
      if (!silent) {
        setAgentNotice(`生产预检完成：待执行 ${r.data?.counts?.assets + r.data?.counts?.keyframes + r.data?.counts?.videos || 0} 项，预计 ${r.data?.totals?.totalCost || 0} 点。`);
      }
    } catch (e) {
      if (!silent) setAgentErr(e.response?.data?.error || '生产预检失败');
    } finally {
      setProductionPlanLoading(false);
    }
  }, [canvasId, imgModelVal, vidModelVal, vidResVal]);

  useEffect(() => {
    refreshProductionPlan({ silent: true });
  }, [refreshProductionPlan, planRefreshVersion]);

  const regenerateSelectedVideo = async () => {
    if (!canvasId || !selectedNode || !['clip', 'video_clip'].includes(selectedNode.data?.agent_role)) return;
    setVideoSubmitLoading(true);
    setAgentErr('');
    setAgentNotice('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/submit-videos`, {
        model: vidModelVal || 'seedance',
        resolution: vidResVal,
        limit: 1,
        nodeIds: [selectedNode.id],
        regenerate: true,
      });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { submitted = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`视频重提 ${submitted.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '视频重提失败');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '视频重提失败');
    } finally {
      setVideoSubmitLoading(false);
    }
  };

  const refreshDeliveryManifest = async () => {
    if (!canvasId) return;
    setDeliveryLoading(true);
    setAgentErr('');
    try {
      const r = await api.get(`/canvas/${canvasId}/agent/delivery-manifest`);
      setDeliveryManifest(r.data);
      setAgentNotice(`镜序清单完成：可交付 ${r.data?.summary?.deliverable || 0} 个，待入库 ${r.data?.summary?.pending_archive || 0} 个。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '交付清单获取失败');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const archiveDeliveryVideos = async () => {
    if (!canvasId) return;
    setDeliveryLoading(true);
    setAgentErr('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/archive-videos`, { limit: 10 });
      const { archived = [], skipped = [], failed = [] } = r.data || {};
      await refreshDeliveryManifest();
      setAgentNotice(`交付成片入库 ${archived.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '部分成片入库失败');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '成片入库失败');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const lockDeliveryVideos = async () => {
    if (!canvasId) return;
    setDeliveryLoading(true);
    setAgentErr('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/lock-delivery`, {});
      await refreshDeliveryManifest();
      setAgentNotice(`已锁定 ${r.data?.locked?.length || 0} 个交付版本。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '锁定交付失败');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const downloadDeliveryPackage = async (format = 'json') => {
    if (!canvasId) return;
    setDeliveryLoading(true);
    setAgentErr('');
    try {
      const r = await api.get(`/canvas/${canvasId}/agent/delivery-package`, {
        params: { format },
        responseType: format === 'json' ? 'json' : 'text',
      });
      const content = format === 'json' ? JSON.stringify(r.data, null, 2) : r.data;
      const type = format === 'csv' ? 'text/csv' : format === 'report' ? 'text/markdown' : 'application/json';
      const ext = format === 'report' ? 'md' : format;
      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `canvas_delivery_${canvasId}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      setAgentNotice(`已导出${format === 'csv' ? '镜序清单' : format === 'report' ? '交付报告' : '交付包 JSON'}。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '导出交付包失败');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const generateAgentKeyframes = async () => {
    if (!canvasId) return;
    setKeyframeLoading(true);
    setAgentErr('');
    setAgentNotice('');
    startProductionPoll(); // 同资产批量：请求超时后靠轮询接回后台陆续完成的首帧
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/generate-keyframes`, {
        model: imgModelVal || 'seedream',
        size: imgSize,
        limit: 10,
      });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { generated = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`首帧完成 ${generated.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '部分首帧生成失败');
    } catch (e) {
      if (e.code === 'ECONNABORTED') {
        setAgentNotice('本批较大，请求已超时但生成仍在后台继续，首帧会陆续出现，不用重复点击。');
      } else {
        setAgentErr(e.response?.data?.error || '批量生成首帧失败');
      }
      await loadCanvas().catch(() => {});
    } finally {
      setKeyframeLoading(false);
    }
  };

  const submitAgentVideos = async () => {
    if (!canvasId) return;
    setVideoSubmitLoading(true);
    setAgentErr('');
    setAgentNotice('');
    try {
      const r = await api.post(`/canvas/${canvasId}/agent/submit-videos`, {
        model: vidModelVal || 'seedance',
        resolution: vidResVal,
        limit: 10,
      });
      await loadCanvas();
      window.dispatchEvent(new Event('credits-updated'));
      const { submitted = [], skipped = [], failed = [] } = r.data || {};
      setAgentNotice(`视频提交 ${submitted.length} 个，跳过 ${skipped.length} 个，失败 ${failed.length} 个。`);
      if (failed.length) setAgentErr(failed[0].error || '部分视频提交失败');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '批量提交视频失败');
    } finally {
      setVideoSubmitLoading(false);
    }
  };

  const persistNodePrompt = async ({ editorText, compiledText = editorText }) => {
    if (!selectedNode || !canvasId || !['imageNode', 'videoNode'].includes(selectedNode.type)) return;
    const dataPatch = {
      prompt_editor_text: editorText,
      prompt_text: compiledText,
      prompt_doc: promptDocFromText(compiledText, promptReferenceBindings),
      reference_bindings: promptReferenceBindings,
    };
    setNodes(current => current.map(node => node.id === selectedId
      ? { ...node, data: { ...node.data, ...dataPatch } }
      : node));
    await api.patch(`/canvas/${canvasId}/nodes/${selectedId}`, { dataPatch });
  };

  // 生成图片（自动拼入关联文本节点内容 + 图类型）
  const generateImage = async () => {
    const view = VIEWS[imgViewIdx];
    const promptBody = [view.prompt, linkedText, imgPrompt.trim()].filter(Boolean).join('，');
    if (!selectedNode || !canvasId || !promptBody) return;
    const fullPrompt = `${promptBody}\n超写实，真人，摄影级质感，8K超高清`;
    const size = imgSize; // 三视图与尺寸解耦，始终用用户所选尺寸
    // 记住这次配置，后续新建图片节点默认沿用（跨会话）
    writeLastConfig(LAST_IMG_CFG_KEY, { model: imgModelVal, size: imgSize, quality: imgQualityVal || undefined });
    setGenerating(true); setGenErr('');
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'generating' } } : n));
    try {
      await persistNodePrompt({ editorText: imgPrompt, compiledText: fullPrompt });
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/generate-image`, { prompt: fullPrompt, size, model: imgModelVal, ...(imgQualityVal ? { imageSize: imgQualityVal } : {}) });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, url: r.data.url, url_expires_at: r.data.url_expires_at, prompt: imgPrompt, status: 'done', model: imgModelVal } } : n));
      window.dispatchEvent(new Event('credits-updated'));
    } catch (e) {
      setGenErr(e.response?.data?.error || '生图失败，请重试');
      setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'failed' } } : n));
    } finally { setGenerating(false); }
  };

  // 压缩图片到 ≤1MB（规避 Nginx body 限制）
  const compressImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      const MAX = 1500;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', 0.82);
    };
    // 图片解码失败(如 HEIC/异常格式)不要卡死，回退用原文件，避免"点了上传没反应"
    img.onerror = () => { URL.revokeObjectURL(objUrl); resolve(file); };
    img.src = objUrl;
  });

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(String(event.target.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const getCanvasInsertPosition = useCallback((index = 0) => {
    const flow = rfRef.current;
    const pane = document.querySelector('.react-flow')?.getBoundingClientRect();
    if (flow?.screenToFlowPosition && pane) {
      return flow.screenToFlowPosition({
        x: pane.left + pane.width / 2 + index * 34,
        y: pane.top + pane.height / 2 + index * 34,
      });
    }
    const viewport = flow?.getViewport?.();
    return {
      x: viewport ? (-viewport.x + 180 + index * 34) / viewport.zoom : 180 + index * 34,
      y: viewport ? (-viewport.y + 140 + index * 34) / viewport.zoom : 140 + index * 34,
    };
  }, []);

  const createImageNodeFromFile = useCallback(async (file, index = 0) => {
    if (!canvasId || !file?.type?.startsWith('image/')) return null;
    let nodeId = null;
    setUploading(true);
    setGenErr('');
    setAgentErr('');
    try {
      const position = getCanvasInsertPosition(index);
      const label = file.name || 'pasted image';
      const created = await api.post(`/canvas/${canvasId}/nodes`, {
        type: 'image',
        x: position.x,
        y: position.y,
        data: { label, status: 'uploading', source: 'upload' },
      });
      nodeId = created.data.id;
      setNodes(nds => [...nds, toFlowNode(created.data)]);
      setSelectedId(nodeId);

      const compressed = await compressImage(file);
      const base64 = await readFileAsBase64(compressed);
      const uploaded = await api.post(`/canvas/${canvasId}/nodes/${nodeId}/upload-image`, { base64, mimeType: 'image/jpeg' });
      setNodes(nds => nds.map(n => n.id === nodeId
        ? { ...n, data: { ...n.data, url: uploaded.data.url, url_expires_at: uploaded.data.url_expires_at, status: 'done', source: 'upload' } }
        : n));
      setAgentNotice('图片已添加到当前画布。');
      return nodeId;
    } catch (e) {
      if (nodeId) {
        setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, status: 'failed' } } : n));
      }
      setAgentErr(e.response?.data?.error || '图片添加失败');
      return null;
    } finally {
      setUploading(false);
    }
  }, [canvasId, getCanvasInsertPosition, setNodes]);

  const uploadImageFilesToCanvas = useCallback(async (files) => {
    const images = Array.from(files || []).filter(file => file?.type?.startsWith('image/'));
    for (let i = 0; i < images.length; i += 1) {
      // Keep order stable when a user selects multiple references.
      await createImageNodeFromFile(images[i], i);
    }
  }, [createImageNodeFromFile]);

  useEffect(() => {
    const handlePaste = (event) => {
      if (!canvasId || !event.clipboardData) return;
      const imageFiles = Array.from(event.clipboardData.items || [])
        .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter(Boolean);
      if (!imageFiles.length) return;
      event.preventDefault();
      uploadImageFilesToCanvas(imageFiles);
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [canvasId, uploadImageFilesToCanvas]);

  // 上传图片
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedNode || !canvasId) return;
    setUploading(true); setGenErr('');
    e.target.value = '';
    try {
      const compressed = await compressImage(file);
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        try {
          const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/upload-image`, { base64, mimeType: 'image/jpeg' });
          setNodes(nds => nds.map(n => n.id === selectedId
            ? { ...n, data: { ...n.data, url: r.data.url, url_expires_at: r.data.url_expires_at, status: 'done', source: 'upload' } } : n));
        } catch (e2) {
          setGenErr(e2.response?.data?.error || '上传失败，请检查图片大小');
        } finally { setUploading(false); }
      };
      reader.readAsDataURL(compressed);
    } catch {
      setGenErr('图片处理失败');
      setUploading(false);
    }
  };

  const readAudioDurationMs = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0;
      URL.revokeObjectURL(url);
      resolve(duration);
    };
    audio.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
    audio.src = url;
  });

  // 上传音频样本或成品到当前音频节点
  const handleAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedNode || selectedNode.type !== 'audioNode' || !canvasId) return;
    if (file.size > 10 * 1024 * 1024) {
      setGenErr('音频不能超过 10MB');
      return;
    }
    setUploading(true); setGenErr('');
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'uploading' } } : n));
    try {
      const [base64, durationMs] = await Promise.all([readFileAsBase64(file), readAudioDurationMs(file)]);
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/upload-audio`, {
        base64,
        mimeType: file.type || (file.name.toLowerCase().endsWith('.mp3') ? 'audio/mpeg' : 'audio/wav'),
        durationMs,
        label: file.name,
      });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, ...r.data, label: file.name, status: 'done', source: 'upload', voice_id: undefined, voice_kind: undefined, rights_confirmed: false } }
        : n));
      setVoiceRightsConfirmed(false);
    } catch (error) {
      setGenErr(error.response?.data?.error || '音频上传失败');
      setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'failed' } } : n));
    } finally {
      setUploading(false);
    }
  };

  // 读外部视频的时长/宽高：本地先校验，别让用户传 50MB 上去才被后端拒
  const readVideoMeta = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const meta = {
        durationMs: Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : 0,
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
      };
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.onerror = () => { URL.revokeObjectURL(url); resolve({ durationMs: 0, width: 0, height: 0 }); };
    video.src = url;
  });

  // 上传外部视频到当前视频节点（当参考视频用）。不扣点。
  // ⚠️ 走原始二进制而不是 base64：base64 膨胀 33%，50MB 文件变 67MB，顶穿火山 64MB 请求体上限。
  // 浏览器能读到的只有时长/宽高，帧率读不到——那条交给后端 ffprobe 判。
  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedNode || selectedNode.type !== 'videoNode' || !canvasId) return;
    const mime = (file.type || '').toLowerCase();
    const isMov = mime === 'video/quicktime' || /\.mov$/i.test(file.name);
    if (mime && mime !== 'video/mp4' && !isMov) {
      setGenErr('参考视频仅支持 mp4 / mov 格式');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setGenErr(`视频 ${(file.size / 1024 / 1024).toFixed(1)}MB，超过 50MB 上限，请先压缩或裁短`);
      return;
    }
    setUploading(true); setGenErr('');
    try {
      const meta = await readVideoMeta(file);
      // 本地能判的先判掉（时长/尺寸/宽高比），帧率和精确面积交给后端 ffprobe
      if (meta.durationMs && meta.durationMs < 2000) {
        throw new Error(`视频 ${(meta.durationMs / 1000).toFixed(1)}s，短于下限 2s`);
      }
      if (meta.durationMs && meta.durationMs > 15000) {
        throw new Error(`视频 ${(meta.durationMs / 1000).toFixed(1)}s，超过单段上限 15s，请先裁短`);
      }
      if (meta.width && meta.height) {
        const minSide = vidSpec?.provider === 'minimax' ? 256 : 300;
        const maxSide = vidSpec?.provider === 'minimax' ? 5760 : 6000;
        if (Math.min(meta.width, meta.height) < minSide) throw new Error(`画面 ${meta.width}×${meta.height}，短边不足 ${minSide}px`);
        if (Math.max(meta.width, meta.height) > maxSide) throw new Error(`画面 ${meta.width}×${meta.height}，长边超过 ${maxSide}px`);
        const area = meta.width * meta.height;
        if (vidSpec?.provider !== 'minimax' && area > 2086876) throw new Error(`画面 ${meta.width}×${meta.height}，总面积超过约 1080p 上限，请先压一版`);
        const ratio = meta.width / meta.height;
        if (ratio < 0.4 || ratio > 2.5) throw new Error(`宽高比 ${ratio.toFixed(2)}，超出 0.4~2.5 范围`);
      }
      setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'uploading' } } : n));
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/upload-video?model=${encodeURIComponent(vidModelVal)}`, file, {
        headers: { 'Content-Type': isMov ? 'video/quicktime' : 'video/mp4' },
        transformRequest: [(data) => data],
      });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, ...r.data, label: file.name, status: 'succeeded', source: 'upload', taskId: undefined } }
        : n));
    } catch (error) {
      setGenErr(error.response?.data?.error || error.message || '视频上传失败');
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, status: n.data?.url ? 'succeeded' : undefined } } : n));
    } finally {
      setUploading(false);
    }
  };

  // 手动重试把参考视频存进我们的存储。正常路径不需要它——提交时后端会自己补存。
  // 这里只是补存失败后的逃生口（小红叹号点一下）。为什么不让用户"重新轮询"：
  // poll-video 只对 status==='processing' 的节点轮询，节点一旦 succeeded
  // 那条自动转存路径就再也走不到了，让用户去轮询等于无路可走。
  const retryArchiveRefVideos = async () => {
    if (!canvasId || !refVideoUnarchived.length) return;
    setArchivingRefVideo(true); setGenErr('');
    const failures = [];
    for (const node of refVideoUnarchived) {
      try {
        const r = await api.post(`/canvas/${canvasId}/nodes/${node.id}/archive-video`, {});
        setNodes(nds => nds.map(n => n.id === node.id
          ? { ...n, data: { ...n.data, ...(r.data.url ? { url: r.data.url } : {}), storage: r.data.storage, storage_key: r.data.storage_key, archived_at: r.data.archived_at, archive_error: null } }
          : n));
      } catch (error) {
        failures.push(error.response?.data?.error || error.message || '补存失败');
      }
    }
    if (failures.length) setGenErr(failures[0]);
    setArchivingRefVideo(false);
  };

  const registerCurrentVoice = async () => {
    if (!selectedNode || selectedNode.type !== 'audioNode' || !canvasId) return;
    setGenerating(true); setGenErr('');
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/register-voice`, {
        model: audModelVal,
        rightsConfirmed: voiceRightsConfirmed,
      });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, voice_id: r.data.voiceId, voice_kind: 'cloned', voice_model: audSpec?.model, model: audModelVal, provider: audSpec?.provider, rights_confirmed: true } }
        : n));
    } catch (error) {
      setGenErr(error.response?.data?.error || '声音注册失败');
    } finally {
      setGenerating(false);
    }
  };

  const generateAudioNode = async () => {
    if (!selectedNode || selectedNode.type !== 'audioNode' || !canvasId) return;
    const text = audTextForGeneration;
    if (!text) return;
    setGenerating(true); setGenErr('');
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'generating' } } : n));
    try {
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/generate-audio`, {
        text,
        model: audModelVal,
        systemVoice: audVoiceVal,
      });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, ...r.data, voice_kind: r.data.voiceKind, transcript: text, model: audModelVal, system_voice: audVoiceVal, status: 'done' } }
        : n));
      window.dispatchEvent(new Event('credits-updated'));
    } catch (error) {
      setGenErr(error.response?.data?.error || '语音生成失败');
      setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'failed' } } : n));
    } finally {
      setGenerating(false);
    }
  };

  // 生成视频
  const generateVideo = async () => {
    if (!selectedNode || !canvasId) return;
    const finalPrompt = [linkedVideoText, vidPrompt.trim()].filter(Boolean).join('，');
    // 记住这次配置，后续新建视频节点默认沿用（跨会话）
    writeLastConfig(LAST_VID_CFG_KEY, { model: vidModelVal, ratio: vidRatio, duration: vidDurVal, resolution: vidResVal });
    setGenerating(true); setGenErr('');
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'processing' } } : n));
    try {
      await persistNodePrompt({ editorText: vidPrompt, compiledText: finalPrompt });
      const r = await api.post(`/canvas/${canvasId}/nodes/${selectedId}/generate-video`, {
        prompt: finalPrompt, aspect_ratio: vidRatio, duration: vidDurVal, model: vidModelVal, resolution: vidResVal, generateAudio: supportsGeneratedAudio && vidAudio,
      });
      setNodes(nds => nds.map(n => n.id === selectedId
        ? { ...n, data: { ...n.data, taskId: r.data.taskId, provider: r.data.provider, cost: r.data.cost, status: 'processing', prompt: vidPrompt, aspect_ratio: vidRatio, duration: r.data.duration || vidDurVal, resolution: r.data.resolution || vidResVal, model: vidModelVal } } : n));
      window.dispatchEvent(new Event('credits-updated'));
    } catch (e) {
      // 区分"后端明确拒绝"（有 error 文案，如参考音频超限/审核不过）与"网络层没拿到响应"
      // （代理超时/502，e.response 为空）——后者旧代码只会显示笼统"请重试"，让人白等还不知原因。
      const backendMsg = e.response?.data?.error;
      const netMsg = !e.response
        ? (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')
          ? '请求超时——视频任务可能仍在后台提交，请等待或刷新查看；若反复超时请缩短时长/减少参考素材后重试'
          : '网络中断，未能确认是否提交成功，请刷新画布查看该节点状态')
        : '视频生成失败，请重试';
      setGenErr(backendMsg || netMsg);
      // 参考视频没能存进我们的存储时后端会带回是哪个节点坏的——就地标上，
      // 用户关掉这条错误后仍能从那个节点上的小红叹号找到该重新上传哪一段。
      const badNodeId = e.response?.data?.node_id;
      setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: { ...n.data, status: 'idle' } } : n));
      if (badNodeId) {
        setNodes(nds => nds.map(n => n.id === badNodeId
          ? { ...n, data: { ...n.data, archive_error: backendMsg || '存入失败' } } : n));
      }
    } finally { setGenerating(false); }
  };

  // 保存文字内容
  const saveText = async () => {
    if (!selectedNode || !canvasId) return;
    const newData = { ...selectedNode.data, content: txtContent };
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: newData } : n));
    await api.patch(`/canvas/${canvasId}/nodes/${selectedId}`, { data: newData }).catch(() => {});
  };

  // 从文本节点一键创建图片节点（自动连线 + 选中）
  const createImageFromText = async () => {
    if (hasBlockingProductionGaps) {
      setGenErr('请先在右侧 Agent 处理素材缺口。');
      return;
    }
    if (!selectedNode || selectedNode.type !== 'textNode' || !canvasId) return;
    // 先保存当前文本
    const newData = { ...selectedNode.data, content: txtContent };
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: newData } : n));
    await api.patch(`/canvas/${canvasId}/nodes/${selectedId}`, { data: newData }).catch(() => {});
    // 在文本节点右侧创建图片节点
    const x = selectedNode.position.x + 320;
    const y = selectedNode.position.y;
    const r = await api.post(`/canvas/${canvasId}/nodes`, { type: 'image', x, y, data: {} });
    setNodes(nds => [...nds, toFlowNode(r.data)]);
    // 连线 文本 → 图片
    const er = await api.post(`/canvas/${canvasId}/edges`, { source: selectedId, target: r.data.id });
    setEdges(eds => addEdge({ source: selectedId, target: r.data.id, id: er.data.id, ...edgeOptions }, eds));
    setSelectedId(r.data.id);
  };

  // 从文本节点一键创建音频节点（自动连线 + 选中）
  const createAudioFromText = async () => {
    if (!selectedNode || selectedNode.type !== 'textNode' || !canvasId) return;
    const newData = { ...selectedNode.data, content: txtContent };
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: newData } : n));
    await api.patch(`/canvas/${canvasId}/nodes/${selectedId}`, { data: newData }).catch(() => {});
    const x = selectedNode.position.x + 320;
    const y = selectedNode.position.y + 90;
    const r = await api.post(`/canvas/${canvasId}/nodes`, { type: 'audio', x, y, data: { label: '台词音频' } });
    setNodes(nds => [...nds, toFlowNode(r.data)]);
    const er = await api.post(`/canvas/${canvasId}/edges`, { source: selectedId, target: r.data.id });
    setEdges(eds => addEdge({ source: selectedId, target: r.data.id, id: er.data.id, ...edgeOptions }, eds));
    setSelectedId(r.data.id);
  };

  // 从文本节点一键创建视频节点（自动连线 + 选中）
  const createVideoFromText = async () => {
    if (hasBlockingProductionGaps) {
      setGenErr('请先在右侧 Agent 处理素材缺口。');
      return;
    }
    if (!selectedNode || selectedNode.type !== 'textNode' || !canvasId) return;
    const newData = { ...selectedNode.data, content: txtContent };
    setNodes(nds => nds.map(n => n.id === selectedId ? { ...n, data: newData } : n));
    await api.patch(`/canvas/${canvasId}/nodes/${selectedId}`, { data: newData }).catch(() => {});
    const x = selectedNode.position.x + 320;
    const y = selectedNode.position.y;
    const r = await api.post(`/canvas/${canvasId}/nodes`, { type: 'video', x, y, data: {} });
    setNodes(nds => [...nds, toFlowNode(r.data)]);
    const er = await api.post(`/canvas/${canvasId}/edges`, { source: selectedId, target: r.data.id });
    setEdges(eds => addEdge({ source: selectedId, target: r.data.id, id: er.data.id, ...edgeOptions }, eds));
    setSelectedId(r.data.id);
  };

  // 保存画布名称
  const saveName = async () => {
    setEditingName(false);
    const name = canvasName.trim() || '未命名画布';
    setCanvasName(name);
    await api.patch(`/canvas/${canvasId}/name`, { name }).catch(() => {});
  };

  // 下载资产
  const downloadAsset = async () => {
    if (!selectedNode?.data?.url) return;
    try {
      const r    = await fetch(selectedNode.data.url);
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      const extension = selectedNode.type === 'videoNode' ? '.mp4' : selectedNode.type === 'audioNode' ? `.${selectedNode.data.format || 'wav'}` : '.png';
      a.download = `canvas_${selectedNode.type}_${Date.now()}${extension}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { window.open(selectedNode.data.url, '_blank'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center text-gray-400 gap-2 bg-[#f4f1e9] dark:bg-[#0a0a0f]" style={{ height: '100vh' }}>
      <Loader2 size={16} className="animate-spin" /> 加载画布…
    </div>
  );

  const isProcessing = selectedNode?.data?.status === 'processing' || selectedNode?.data?.status === 'generating';
  const hasAsset     = !!selectedNode?.data?.url;
  const nodeRows = nodes.map(n => {
    const label = n.data?.label || n.data?.name || (
      n.type === 'textNode' ? '文本节点' : n.type === 'imageNode' ? '图片节点' : n.type === 'audioNode' ? '音频节点' : n.type === 'groupNode' ? '场次组' : '视频节点'
    );
    const status = n.data?.status || (n.data?.url ? 'done' : 'pending');
    const Icon = n.type === 'textNode' ? Type : n.type === 'imageNode' ? ImageIcon : n.type === 'audioNode' ? Volume2 : n.type === 'pose3dNode' ? Boxes : n.type === 'groupNode' ? Box : Video;
    return { id: n.id, label, status, type: n.type, Icon };
  });
  const assetRows = nodes
    .filter(n => n.data?.agent_role === 'asset')
    .map(n => ({
      id: n.id,
      label: n.data?.asset?.name || n.data?.label || '资产',
      assetType: n.data?.asset?.type || 'asset',
      status: n.data?.status || 'pending',
      confidence: n.data?.asset?.confidence,
    }));
  const shotRows = nodes.filter(n => n.data?.agent_role === 'shot_task');
  const sourceScriptNode = nodes.find(n => n.data?.agent_role === 'script_source' && n.data?.analysis_summary);
  const latestAgentAnalysis = agentAnalysis || (sourceScriptNode ? {
    analysis_source: sourceScriptNode.data?.analysis_source,
    reliability: sourceScriptNode.data?.analysis_summary,
  } : null);
  const keyframeRows = nodes.filter(n => n.data?.agent_role === 'keyframe');
  const clipRows = nodes.filter(n => ['clip', 'video_clip'].includes(n.data?.agent_role));
  const acceptedShotCount = shotRows.filter(n => n.data?.status === 'accepted').length;
  const pendingAssetRefCount = nodes
    .filter(n => n.data?.agent_role === 'asset')
    .filter(n => n.data?.status === 'accepted')
    .filter(n => !n.data?.url && !['generating', 'done', 'succeeded'].includes(n.data?.status))
    .length;
  const qcReadyAssetCount = nodes
    .filter(n => n.data?.agent_role === 'asset')
    .filter(n => n.data?.status === 'accepted')
    .filter(n => n.data?.url)
    .filter(n => ['usable', 'locked'].includes(n.data?.qc_status))
    .length;
  const qcNeedsRegenCount = nodes
    .filter(n => n.data?.agent_role === 'asset')
    .filter(n => ['needs_regen', 'retry_recommended'].includes(n.data?.qc_status))
    .length;
  const keyframeReadyCount = keyframeRows
    .filter(n => n.data?.url)
    .filter(n => ['usable', 'locked'].includes(n.data?.qc_status))
    .length;
  const keyframeNeedsRegenCount = keyframeRows
    .filter(n => ['needs_regen', 'retry_recommended'].includes(n.data?.qc_status))
    .length;
  const pendingKeyframeCount = keyframeRows.filter(n => !n.data?.url && !['generating', 'done', 'succeeded'].includes(n.data?.status)).length;
  const hasReadyImageForClip = (clip) => edges
    .filter(e => e.target === clip.id)
    .some(e => {
      const source = nodes.find(n => n.id === e.source);
      return ['image', 'imageNode'].includes(source?.type)
        && !!source.data?.url
        && ['usable', 'locked'].includes(source.data?.qc_status);
    });
  const readyClipCount = clipRows
    .filter(n => !n.data?.taskId && !n.data?.url && !['processing', 'succeeded'].includes(n.data?.status))
    .filter(hasReadyImageForClip)
    .length;
  const processingClipCount = clipRows.filter(n => n.data?.status === 'processing').length;
  const succeededClipCount = clipRows.filter(n => n.data?.status === 'succeeded' || n.data?.url).length;
  const submittedClipCount = clipRows.filter(n => n.data?.taskId).length;
  const videoTaskText = processingClipCount > 0
    ? '生成中'
    : succeededClipCount > 0
      ? `${succeededClipCount} 个成功`
      : readyClipCount > 0
        ? `${readyClipCount} 个待提交`
        : submittedClipCount > 0
          ? `${submittedClipCount} 个已提交`
          : '未发起';
  const retryRows = nodes.filter(n => ['failed', 'retry_recommended'].includes(n.data?.status) || n.data?.qc_status === 'retry_recommended');
  const statusLabel = (node) => {
    if (!node) return '缺失';
    if (node.data?.status === 'processing' || node.data?.status === 'generating') return '生成中';
    if (node.data?.status === 'failed' || node.data?.status === 'rejected') return '失败';
    if (node.data?.url || ['done', 'succeeded'].includes(node.data?.status)) {
      return ['usable', 'locked'].includes(node.data?.qc_status) ? '可用' : '待质检';
    }
    if (node.data?.status === 'accepted') return '已确认';
    return '待处理';
  };
  const shotProgressRows = shotRows.map(shot => {
    const shotAssets = edges
      .filter(e => e.target === shot.id)
      .map(e => nodes.find(n => n.id === e.source))
      .filter(n => n?.data?.agent_role === 'asset');
    const keyframe = keyframeRows.find(n => n.data?.shot_id === shot.data?.shot_id);
    const clips = clipRows.filter(n => n.data?.shot_id === shot.data?.shot_id);
    const clip = clips.find(n => n.data?.status === 'processing') || clips.find(n => n.data?.url) || clips[0];
    const assetReady = shotAssets.length && shotAssets.every(n => n.data?.url && ['usable', 'locked'].includes(n.data?.qc_status));
    return {
      id: shot.id,
      label: shot.data?.label || shot.data?.shot_id || '片段',
      asset: shotAssets.length ? `${shotAssets.filter(n => n.data?.url && ['usable', 'locked'].includes(n.data?.qc_status)).length}/${shotAssets.length}` : '0/0',
      assetReady,
      keyframe: statusLabel(keyframe),
      video: statusLabel(clip),
      cost: [keyframe, clip, ...shotAssets].reduce((sum, n) => sum + (Number(n?.data?.cost) || 0), 0),
      blocker: !assetReady ? '资产' : !keyframe?.data?.url ? '首帧' : !['usable', 'locked'].includes(keyframe?.data?.qc_status) ? '首帧质检' : !clip?.data?.url ? '视频' : '',
    };
  });
  const productionPlanItems = productionPlan
    ? [
        ...(productionPlan.queue?.assets || []),
        ...(productionPlan.queue?.keyframes || []),
        ...(productionPlan.queue?.videos || []),
      ]
    : [];
  const productionSkippedItems = productionPlan
    ? [
        ...(productionPlan.skipped?.assets || []),
        ...(productionPlan.skipped?.keyframes || []),
        ...(productionPlan.skipped?.videos || []),
      ]
    : [];
  const productionBlockedGaps = productionPlan
    ? Array.from(new Map([
        ...(productionPlan.blocked_gaps || []),
        ...(productionPlan.gaps || []).filter(gap => gap?.blocking),
      ].map((gap, index) => [gap?.gap_id || `${gap?.asset_type}-${gap?.asset_name}-${index}`, gap])).values())
    : [];
  // 缺口改为"软提示不硬拦"：不再禁用任何上传/生成按钮；缺素材的镜头会在生成时各自跳过，
  // 素材清单仍显示缺口提醒。保留 productionBlockedGaps 以备它用。
  const hasBlockingProductionGaps = false;
  void productionBlockedGaps;
  const statusText = (status) => {
    if (status === 'processing' || status === 'generating') return '生成中';
    if (status === 'succeeded' || status === 'done' || status === 'accepted') return '已完成';
    if (status === 'failed' || status === 'rejected') return '失败';
    return '待处理';
  };
  const directoryFilters = [
    { key: 'all', label: '全部' },
    { key: 'shot', label: '片段' },
    { key: 'asset', label: '素材' },
    { key: 'keyframe', label: '首帧' },
    { key: 'audio', label: '音频' },
    { key: 'video', label: '视频' },
    { key: 'issue', label: '问题' },
  ];
  const directoryRows = nodes.map(n => {
    const label = n.data?.asset?.name || n.data?.label || n.data?.name || (
      n.type === 'textNode' ? '文本节点' : n.type === 'imageNode' ? '图片节点' : n.type === 'audioNode' ? '音频节点' : n.type === 'groupNode' ? '场次组' : '视频节点'
    );
    const status = n.data?.status || (n.data?.url ? 'done' : 'pending');
    const hasIssue = ['failed', 'rejected', 'error'].includes(status) || ['needs_regen', 'retry_recommended', 'rejected'].includes(n.data?.qc_status);
    const category = hasIssue
      ? 'issue'
      : n.data?.agent_role === 'keyframe'
        ? 'keyframe'
      : n.data?.agent_role === 'shot_task' || n.data?.agent_role === 'script_source' || n.type === 'textNode'
        ? 'shot'
        : n.data?.agent_role === 'asset' || n.type === 'imageNode'
          ? 'asset'
          : n.type === 'audioNode'
            ? 'audio'
          : ['clip', 'video_clip'].includes(n.data?.agent_role) || n.type === 'videoNode'
              ? 'video'
              : 'asset';
    const Icon = category === 'shot' ? Type : category === 'video' ? Video : category === 'audio' ? Volume2 : category === 'keyframe' ? Sparkles : category === 'issue' ? CircleAlert : ImageIcon;
    const searchText = [
      label,
      status,
      statusText(status),
      n.data?.qc_status,
      n.data?.agent_role,
      n.data?.scene_id,
      n.data?.shot_id,
      n.data?.asset?.type,
      n.data?.asset?.name,
      n.data?.content,
      n.data?.prompt,
    ].filter(Boolean).join(' ').toLowerCase();
    return { id: n.id, label, status, category, Icon, searchText };
  });
  const filteredDirectoryRows = directoryRows.filter(row => {
    const passFilter = directoryFilter === 'all' || row.category === directoryFilter;
    const query = directorySearch.trim().toLowerCase();
    const passSearch = !query || row.searchText.includes(query);
    return passFilter && passSearch;
  });
  const focusCanvasNode = (id) => {
    const node = nodes.find(item => item.id === id);
    setSelectedId(id);
    setDirectoryOpen(false);
    if (!node) return;
    const width = node.width || 220;
    const height = node.height || 140;
    try {
      rfRef.current?.setCenter?.(node.position.x + width / 2, node.position.y + height / 2, {
        zoom: Math.max(rfRef.current?.getZoom?.() || 1, 0.85),
        duration: 320,
      });
    } catch {
      try { rfRef.current?.fitView?.({ nodes: [{ id }], padding: 0.45, duration: 320 }); } catch {}
    }
  };
  const showRetryAdvice = () => {
    setAgentErr('');
    if (!retryRows.length) {
      setAgentNotice('当前没有失败或建议重试的节点。');
      return;
    }
    const first = retryRows[0];
    setSelectedId(first.id);
    setAgentNotice(`建议先检查「${first.data?.label || '失败节点'}」：${first.data?.qc_note || '简化动作、减少同框角色或补充参考图后重试。'}`);
  };

  const prepareAgentAction = async (actionType) => {
    setAgentErr('');
    const payloadByType = {
      generate_assets: { model: imgModelVal || 'seedream', size: imgSize, limit: 10 },
      generate_keyframes: { model: imgModelVal || 'seedream', size: imgSize, limit: 10 },
      submit_videos: { model: vidModelVal || 'seedance', resolution: vidResVal, generateAudio: supportsGeneratedAudio && vidAudio, limit: 10 },
    };
    try {
      const action = await canvasAgent.createAction(actionType, payloadByType[actionType] || {});
      setAgentNotice(`已创建确认卡：${action?.estimated_cost > 0 ? `预计 ${action.estimated_cost} 点` : '不扣点'}。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '确认卡创建失败');
    }
  };

  const generateGapReference = async (gap) => {
    if (!canvasId || !gap?.gap_id) return;
    setAssetRefLoading(true);
    setGeneratingGapIds(set => new Set(set).add(gap.gap_id));
    if (gap.nodeId) setNodes(nds => nds.map(n => n.id === gap.nodeId ? { ...n, data: { ...n.data, status: 'generating' } } : n));
    setAgentErr('');
    setAgentNotice('');
    try {
      const response = await api.post(`/canvas/${canvasId}/agent/gaps/${gap.gap_id}/generate`, {
        nodeId: gap.nodeId,
        model: imgModelVal || 'seedream',
        size: imgSize,
        regenerate: true, // 这条入口是"换一张/重试"，强制重新生成，不要短路返回老图
        styleKey: agentStyle || undefined, // 套用面板选的题材画风，治老画布出现代人
        assetName: gap.asset_name, // 缺失素材：后端据此现建节点并连回分镜
        assetType: gap.asset_type_key,
      }, { timeout: 120000 }); // 卡死的请求120s后失败掉，不再永远"生成中"
      // 只更新这一个节点，不整张画布/对话全量重拉
      const url = response.data?.url;
      if (gap.nodeId) setNodes(nds => nds.map(n => n.id === gap.nodeId
        ? { ...n, data: { ...n.data, ...(url ? { url } : {}), status: 'accepted', qc_status: 'usable' } } : n));
      else await loadCanvas(); // 缺失素材是新建的节点，拉一次把节点+连线显示出来
      window.dispatchEvent(new Event('credits-updated'));
      setPlanRefreshVersion(version => version + 1);
      const cost = response.data?.cost || 0;
      setAgentNotice(cost > 0 ? `已生成参考图，扣除 ${cost} 点。` : '该参考图已经可用。');
    } catch (e) {
      if (gap.nodeId) setNodes(nds => nds.map(n => n.id === gap.nodeId ? { ...n, data: { ...n.data, status: 'failed' } } : n));
      setAgentErr(e.response?.data?.error || '缺口参考图生成失败');
    } finally {
      setAssetRefLoading(false);
      setGeneratingGapIds(set => { const next = new Set(set); next.delete(gap.gap_id); return next; });
    }
  };

  // 停止/重置卡住的"生成中"素材：复位为失败，用户可重新生成或上传
  const resetGap = async (gap) => {
    if (!canvasId || !gap?.nodeId) return;
    try {
      await api.patch(`/canvas/${canvasId}/agent/nodes/${gap.nodeId}/status`, {
        status: 'failed', qc_status: 'retry_recommended', qc_note: '用户手动停止/重置',
      });
      setNodes(nds => nds.map(n => n.id === gap.nodeId ? { ...n, data: { ...n.data, status: 'failed', qc_status: 'retry_recommended' } } : n));
      setGeneratingGapIds(set => { const next = new Set(set); next.delete(gap.gap_id); return next; });
      setPlanRefreshVersion(version => version + 1);
      setAgentNotice('已停止，可重新生成或上传。');
    } catch (e) {
      setAgentErr(e.response?.data?.error || '重置失败');
    }
  };

  // 跳过/恢复素材：跳过 = 不做参考图，视频按剧情文字生成（路人、小道具、次要场景）；不挡生成
  const skipGap = async (gap, skip) => {
    if (!canvasId || !gap?.nodeId) { if (skip) setAgentErr('这个素材还没有画布节点，先点"自动生成"或上传一次即可'); return; }
    const nextStatus = skip ? 'skipped' : 'missing';
    try {
      await api.patch(`/canvas/${canvasId}/agent/nodes/${gap.nodeId}/status`, {
        status: nextStatus, qc_note: skip ? '用户跳过，视频按剧情文字生成' : '',
      });
      setNodes(nds => nds.map(n => n.id === gap.nodeId ? { ...n, data: { ...n.data, status: nextStatus } } : n));
      setPlanRefreshVersion(version => version + 1);
      setAgentNotice(skip ? `已跳过「${gap.asset_name || '该素材'}」，视频会按剧情文字生成。` : `已恢复「${gap.asset_name || '该素材'}」。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || '操作失败');
    }
  };

  const uploadGapReference = async (gap, file) => {
    if (!canvasId || !gap?.gap_id) return;
    if (!file?.type?.startsWith('image/')) { setAgentErr('请选择图片文件（jpg/png）'); return; }
    const assetName = gap.asset_name || gap.name || '该素材';
    setUploading(true);
    setAgentErr('');
    setAgentNotice(`正在上传图片到「${assetName}」…`);
    try {
      const compressed = await compressImage(file);
      const base64 = await readFileAsBase64(compressed);
      const r = await api.post(`/canvas/${canvasId}/agent/gaps/${gap.gap_id}/upload-image`, {
        nodeId: gap.nodeId,
        base64,
        mimeType: 'image/jpeg',
        assetName: gap.asset_name, // 缺失素材：后端据此现建节点并连回分镜
        assetType: gap.asset_type_key,
      }, { timeout: 120000 });
      // 只更新这一个节点，不整张画布重拉
      const url = r.data?.url;
      if (gap.nodeId) setNodes(nds => nds.map(n => n.id === gap.nodeId
        ? { ...n, data: { ...n.data, ...(url ? { url } : {}), status: 'accepted', qc_status: 'usable', source: 'upload' } } : n));
      else await loadCanvas(); // 缺失素材是新建节点，拉一次显示节点+连线
      setGeneratingGapIds(set => { const next = new Set(set); next.delete(gap.gap_id); return next; });
      setPlanRefreshVersion(version => version + 1);
      setAgentNotice(`已用你的图片作为「${assetName}」，Agent 已可识别。`);
    } catch (e) {
      setAgentErr(e.response?.data?.error || `「${assetName}」图片上传失败`);
    } finally {
      setUploading(false);
    }
  };

  const updateSelectedAgentQc = async (qcStatus, note) => {
    if (!selectedNode) return;
    if (selectedNode.data?.agent_role === 'asset') return updateAssetQc(qcStatus, note);
    if (selectedNode.data?.agent_role === 'keyframe') return updateKeyframeQc(qcStatus, note);
    if (['clip', 'video_clip'].includes(selectedNode.data?.agent_role)) return updateVideoQc(qcStatus, note);
  };

  return (
    <div className="flex flex-col bg-[#f4f0e7] dark:bg-[#0a0a0f]" style={{ height: '100vh' }}>

      {/* ── 顶部 Header ── */}
      <div className="h-12 shrink-0 flex items-center gap-3 px-5 border-b border-[#ded6c8] dark:border-white/[0.06] bg-[#fbf8f1] dark:bg-[#0c0c10]">
        <button
          onClick={() => navigate('/canvas')}
          className="text-stone-400 hover:text-stone-700 dark:hover:text-gray-200 transition-colors p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-white/[0.06]"
        >
          <ArrowLeft size={15} />
        </button>
        <span className="text-[13px] text-stone-400 dark:text-gray-600">/</span>
        {editingName ? (
          <input
            autoFocus
            value={canvasName}
            onChange={e => setCanvasName(e.target.value)}
            onBlur={saveName}
            onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
            className="text-[13px] font-medium text-stone-800 dark:text-gray-200 bg-transparent border-b border-[#8c7db0] focus:outline-none px-0.5 min-w-[120px]"
          />
        ) : (
          <button
            onClick={() => setEditingName(true)}
            title="点击重命名"
            className="text-[13px] font-medium text-stone-800 dark:text-gray-300 hover:text-[#6f6196] dark:hover:text-[#b9afe7] transition-colors px-1 py-0.5 rounded hover:bg-stone-100 dark:hover:bg-white/[0.06]"
          >
            {canvasName || '未命名画布'}
          </button>
        )}
        {me && (
          <div className="ml-auto flex items-center gap-2 text-[11px] text-stone-500 dark:text-gray-400">
            <span className="hidden sm:inline">{me.username}</span>
          </div>
        )}
      </div>

      {/* ── 工作台 ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        <div className="relative z-30 w-14 shrink-0 border-r border-[#ded6c8] bg-[#fbf8f1] dark:bg-[#0c0c10] dark:border-white/[0.06] flex flex-col items-center py-3">
          <button
            type="button"
            onClick={() => setDirectoryOpen(true)}
            title="打开画布目录"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#ded6c8] bg-white/80 text-stone-600 shadow-sm hover:border-[#c9bedc] hover:text-[#6f6196] dark:bg-white/[0.04]"
          >
            <Search size={17} />
          </button>
          <span className="mt-2 text-[10px] text-stone-400">目录</span>

          {directoryOpen && (
            <div className="absolute left-full top-0 z-40 h-full w-[340px] border-r border-[#ded6c8] bg-[#fbf8f1] shadow-2xl shadow-stone-900/10 dark:border-white/[0.06] dark:bg-[#0c0c10]">
              <div className="flex h-full flex-col">
                <div className="border-b border-[#e4dccf] px-4 py-4 dark:border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#ded6c8] bg-white text-stone-500 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-gray-400">
                      <Search size={15} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[14px] font-semibold text-stone-800 dark:text-gray-100">画布目录</p>
                      <p className="text-[11px] text-stone-400">搜索和定位节点</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDirectoryOpen(false)}
                      className="ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-stone-400 hover:bg-[#eee9de] hover:text-stone-700"
                      title="关闭目录"
                    >
                      <X size={15} />
                    </button>
                  </div>

                  <div className="mt-4 flex items-center gap-2 rounded-xl border border-[#ded6c8] bg-white px-3 py-2 dark:border-white/[0.08] dark:bg-white/[0.05]">
                    <Search size={14} className="shrink-0 text-stone-400" />
                    <input
                      value={directorySearch}
                      onChange={event => setDirectorySearch(event.target.value)}
                      placeholder="搜角色、片段、场景、道具、状态"
                      className="min-w-0 flex-1 bg-transparent text-[13px] text-stone-700 placeholder:text-stone-400 outline-none dark:text-gray-200"
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {directoryFilters.map(filter => (
                      <button
                        key={filter.key}
                        type="button"
                        onClick={() => setDirectoryFilter(filter.key)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          directoryFilter === filter.key
                            ? 'border-[#c9bedc] bg-[#ece7f1] text-[#675b89] dark:border-white/[0.16] dark:bg-white/[0.1] dark:text-[#b9afe7]'
                            : 'border-[#ded6c8] bg-white/70 text-stone-500 hover:bg-[#f5f0e7] dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-gray-400 dark:hover:bg-white/[0.07]'
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-3">
                  {filteredDirectoryRows.length === 0 ? (
                    <p className="px-2 py-8 text-center text-[12px] text-stone-400">没有匹配的节点。</p>
                  ) : filteredDirectoryRows.map(({ id, label, status, category, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => focusCanvasNode(id)}
                      className={`mb-2 flex h-12 w-full items-center gap-2 rounded-xl border px-2 text-left transition-colors ${
                        selectedId === id
                          ? 'border-[#c9bedc] bg-[#ece7f1] dark:border-white/[0.16] dark:bg-white/[0.1]'
                          : 'border-transparent bg-white/60 hover:border-[#ded6c8] hover:bg-[#f5f0e7] dark:bg-white/[0.04] dark:hover:border-white/[0.08] dark:hover:bg-white/[0.07]'
                      }`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#e1d9ca] bg-white text-stone-500 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-gray-400"><Icon size={15} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] text-stone-800 dark:text-gray-200">{label}</span>
                        <span className="block truncate text-[10px] text-stone-400">{directoryFilters.find(item => item.key === category)?.label || '节点'} · {statusText(status)}</span>
                      </span>
                      {['failed', 'rejected'].includes(status)
                        ? <CircleAlert size={15} className="shrink-0 text-amber-600" />
                        : ['done', 'succeeded', 'accepted'].includes(status)
                          ? <CheckCircle size={15} className="shrink-0 text-emerald-500" />
                          : null}
                    </button>
                  ))}
                </div>

                <div className="border-t border-[#e4dccf] px-4 py-3 text-[11px] text-stone-400 dark:border-white/[0.06]">
                  {filteredDirectoryRows.length}/{nodes.length} 个节点
                </div>
              </div>
            </div>
          )}

          {/* 资产库入口（对标 Nemo「画布/资产」tab） */}
          <button
            type="button"
            onClick={() => { setAssetPanelOpen(true); setDirectoryOpen(false); }}
            title="打开资产库"
            className="mt-4 flex h-11 w-11 items-center justify-center rounded-xl border border-[#ded6c8] bg-white/80 text-stone-600 shadow-sm hover:border-[#c9bedc] hover:text-[#6f6196] dark:bg-white/[0.04]"
          >
            <FolderOpen size={17} />
          </button>
          <span className="mt-2 text-[10px] text-stone-400">资产</span>

          {assetPanelOpen && (
            <div className="absolute left-full top-0 z-40 h-full w-[360px] border-r border-[#ded6c8] bg-[#fbf8f1] shadow-2xl shadow-stone-900/10 dark:border-white/[0.06] dark:bg-[#0c0c10]">
              <CanvasAssetPanel
                onClose={() => setAssetPanelOpen(false)}
                onUseAsset={useAssetOnCanvas}
              />
            </div>
          )}
        </div>

        {/* ── 画布 ── */}
        <div className="flex-1 relative min-w-0">
        <ReactFlow
          nodes={nodes}
          edges={edgesWithCut}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={edgeOptions}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(_, n) => setSelectedId(n.id)}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={() => { setSelectedId(null); setAddMenuOpen(false); setNodeMenu(null); }}
          onNodeDragStop={onNodeDragStop}
          onNodesDelete={onNodesDelete}
          onEdgesDelete={onEdgesDelete}
          onInit={inst => { rfRef.current = inst; }}
          colorMode={isDark ? 'dark' : 'light'}
          fitView
          deleteKeyCode="Delete"
          connectionRadius={30}
          style={{ height: '100%' }}
        >
          <Background variant="dots" color={isDark ? '#1e1e2a' : '#d8d0c1'} gap={28} size={1.1} />
          <Controls position="bottom-left" />
          <MiniMap nodeColor="#8b7ab8" maskColor="rgba(244,240,231,0.72)" position="bottom-left" pannable zoomable style={{ left: 52, bottom: 16, borderRadius: 12, overflow: 'hidden' }} />

          {/* 工具栏：+ 菜单 */}
          <Panel position="top-left">
            <div className="mt-3 ml-3 relative">
              <button
                onClick={() => setAddMenuOpen(v => !v)}
                 className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#fbf8f1] dark:bg-[#1c1c26] border border-[#ded6c8] dark:border-white/[0.08] text-[13px] text-stone-700 dark:text-gray-400 hover:text-[#6f6196] dark:hover:text-[#b9afe7] hover:border-[#c9bedc] dark:hover:border-white/[0.16] hover:bg-[#eee9de] dark:hover:bg-white/[0.08] shadow-sm transition-all"
              >
                <Plus size={13} className="text-[#6f6196] dark:text-[#b9afe7]" /> 添加节点
              </button>
              {addMenuOpen && (
                <div className="absolute top-full left-0 mt-3 bg-[#fbf8f1] dark:bg-[#1c1c26] border border-[#ded6c8] dark:border-white/[0.08] rounded-lg shadow-xl overflow-hidden z-50 min-w-[210px] p-2">
                  {[
                    { type: 'text',  icon: Type,      label: '文本', desc: '剧本、分镜说明' },
                    { type: 'image', icon: ImageIcon,  label: '图片', desc: '首帧、角色、场景' },
                    { type: 'audio', icon: Volume2,    label: '音频', desc: '上传、配音、固定角色声线' },
                    { type: 'video', icon: Video,      label: '视频', desc: '片段、动态镜头' },
                    { type: 'pose3d', icon: Boxes, label: '3D导演台', desc: '摆小人·定机位·出参考图', accent: true },
                  ].map(({ type, icon: Icon, label, desc, accent }) => (
                    <button
                      key={type}
                      onClick={() => addNode(type)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${accent
                        ? 'mt-1 border-t border-[#ded6c8] pt-3 text-[#76549b] hover:bg-[#efe6fa] dark:border-white/[0.08] dark:text-[#c7a8ef] dark:hover:bg-[#8f62bd]/15'
                        : 'text-stone-700 hover:bg-[#eee9de] dark:text-gray-300 dark:hover:bg-white/[0.04]'}`}
                    >
                      <Icon size={14} className="shrink-0 text-[#6f6196] dark:text-[#b9afe7]" />
                      <span className="min-w-0">
                        <span className="block text-[13px]">{label}</span>
                        {accent && <span className="block text-[10px] text-[#9a83b4] dark:text-[#9277af]">{desc}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Panel>

          {/* 提示 */}
          <Panel position="top-right">
            <p className="text-[11px] text-stone-400 dark:text-gray-700 mt-3 mr-3 text-right leading-5">
              点击节点选中 · Delete 删除<br />
              连线：文本 → 图片/音频 → 视频 / 3D导演台
            </p>
          </Panel>
        </ReactFlow>

        {/* ── 节点右键菜单（对标 libtv）── */}
        {nodeMenu && (
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => setNodeMenu(null)} onContextMenu={e => { e.preventDefault(); setNodeMenu(null); }} />
            <div
              className="fixed z-[61] min-w-[190px] overflow-hidden rounded-xl border border-[#ded6c8] bg-[#fbf8f1] py-1 shadow-2xl shadow-stone-900/20 dark:border-white/[0.08] dark:bg-[#1a1a22]"
              style={{ left: Math.min(nodeMenu.x, window.innerWidth - 210), top: Math.min(nodeMenu.y, window.innerHeight - 260) }}
            >
              {nodeMenu.node?.type === 'imageNode' && (() => {
                const st = nodeMenu.node.data?.liveness_status;
                const label = st === 'active' ? 'Seedance2.0 合规校验 ✓'
                  : st === 'processing' ? '校验中…'
                    : st === 'failed' ? 'Seedance2.0 合规校验（重试）' : 'Seedance2.0 合规校验';
                return (
                  <>
                    <button
                      type="button"
                      disabled={st === 'active' || st === 'processing'}
                      onClick={() => { verifyNodeLiveness(nodeMenu.node.id); setNodeMenu(null); }}
                      title={'校验通过后，这张图可放心用于 Seedance 2.0 视频生成\n\n待校验图像要求：\n格式 jpeg/png/webp/bmp/tiff/gif/heic\n宽高比 0.4~2.5\n边长 300~6000px\n单张小于 30MB'}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#eee9de] disabled:opacity-45 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-white/[0.06]"
                    >
                      <Shield size={13} className="shrink-0 text-[#4a90d9]" />{label}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setNodeMenu(null); depositToAssetLibrary(nodeMenu.node, nodeMenu.node.data?.asset?.type || 'character'); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#eee9de] dark:text-gray-300 dark:hover:bg-white/[0.06]"
                    >
                      <Upload size={13} className="shrink-0 text-stone-400" />保存到我的资产
                    </button>
                    <div className="my-1 border-t border-[#e4dccf] dark:border-white/[0.07]" />
                    <button
                      type="button"
                      onClick={() => { setNodeMenu(null); copyNodeImage(nodeMenu.node); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#eee9de] dark:text-gray-300 dark:hover:bg-white/[0.06]"
                    >
                      <ImageIcon size={13} className="shrink-0 text-stone-400" />复制图片
                    </button>
                  </>
                );
              })()}
              <button
                type="button"
                onClick={() => { setNodeMenu(null); duplicateNode(nodeMenu.node); }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#eee9de] dark:text-gray-300 dark:hover:bg-white/[0.06]"
              >
                <Plus size={13} className="shrink-0 text-stone-400" />创建副本
              </button>
            </div>
          </>
        )}

        {/* ── 3D 导演台同源子应用弹窗 ── */}
        {directorNodeId && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/65 p-2 backdrop-blur-sm">
            <div className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111117] shadow-2xl">
              {/* 标题/关闭都用子应用自己的白色头（其 X 经 director-desk-close 消息触发保存并关闭），宿主不再叠一条 */}
              {(directorBusy || directorClosing) && (
                <div className="pointer-events-none absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-[11px] text-[#c7a8ef] shadow-lg backdrop-blur-sm">
                  <Loader2 size={12} className="animate-spin" />{directorClosing ? '正在保存工程…' : '正在转存截图…'}
                </div>
              )}
              {directorError && (
                <div className="shrink-0 border-b border-red-400/20 bg-red-500/10 px-4 py-2 text-[11px] text-red-300">{directorError}</div>
              )}
              <iframe
                ref={directorIframeRef}
                title="3D导演台"
                src={`/director-desk/index.html?host=xiaoying&theme=${isDark ? 'dark' : 'light'}`}
                className="min-h-0 flex-1 border-0 bg-[#111117]"
                allow="fullscreen"
              />
              {/* 停靠在 iframe 下方独立成行：3D 台自己的工具条也浮在视口底部，悬浮会互相压住 */}
              <div className="shrink-0 border-t border-white/10 bg-[#0e0d13] px-3 py-2.5">
                {directorAgentHistoryOpen && directorAgentHistory.length > 0 && (
                  <div className="mb-2 max-h-52 space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-[#14131a]/95 p-3 text-[11px] shadow-2xl backdrop-blur-xl">
                    {directorAgentHistory.map((turn, index) => (
                      <div key={`${index}_${turn.instruction}`} className="space-y-1 border-b border-white/[0.06] pb-2 last:border-0 last:pb-0">
                        <p className="text-white/55">你：{turn.instruction}</p>
                        <p className="text-[#c7a8ef]">导演 Agent：{turn.reply}</p>
                      </div>
                    ))}
                  </div>
                )}
                {directorAgentReply && (
                  <button
                    type="button"
                    onClick={() => setDirectorAgentHistoryOpen(open => !open)}
                    className="mb-2 block max-w-full rounded-xl border border-[#8f62bd]/30 bg-[#211a2a]/95 px-3 py-2 text-left text-[11px] leading-5 text-[#d8c4ef] shadow-xl backdrop-blur-xl"
                    title="点击展开对话历史"
                  >
                    {directorAgentReply}
                  </button>
                )}
                <form
                  onSubmit={(event) => { event.preventDefault(); void runDirectorPoseAgent(); }}
                  className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#17161d]/95 p-2 shadow-2xl backdrop-blur-xl"
                >
                  <input
                    value={directorAgentInput}
                    onChange={event => setDirectorAgentInput(event.target.value)}
                    disabled={directorAgentLoading || directorClosing}
                    maxLength={2000}
                    placeholder="描述场面调度，比如：角色01坐下，角色02站在他身后"
                    className="min-w-0 flex-1 bg-transparent px-2 py-2 text-[12px] text-white outline-none placeholder:text-white/35 disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={directorAgentLoading || directorClosing || !directorAgentInput.trim()}
                    className="flex h-9 shrink-0 items-center gap-1.5 rounded-xl bg-[#76549b] px-3 text-[11px] font-medium text-white transition-colors hover:bg-[#865fb0] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {directorAgentLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {directorAgentLoading ? '思考中…' : '发送'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ── 底部生成面板 ── */}
        {selectedNode && selectedNode.type !== 'pose3dNode' && (
          <div className="absolute bottom-0 left-0 right-0 z-40 p-3">
            <div className="max-w-[640px] mx-auto rounded-lg border border-[#ded6c8] dark:border-white/[0.08] shadow-2xl bg-[#fbf8f1] dark:bg-[#1a1a22]">

              {/* 面板头 */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-white/[0.06]">
                {selectedNode.type === 'imageNode' && <><ImageIcon size={12} className="text-[#8c7db0]" /><span className="text-[12px] text-gray-700 dark:text-gray-300 font-medium">图片节点</span></>}
                {selectedNode.type === 'videoNode' && <><Video     size={12} className="text-[#8c7db0]" /><span className="text-[12px] text-gray-700 dark:text-gray-300 font-medium">视频节点</span></>}
                {selectedNode.type === 'audioNode' && <><Volume2  size={12} className="text-[#8c7db0]" /><span className="text-[12px] text-gray-700 dark:text-gray-300 font-medium">音频节点</span></>}
                {selectedNode.type === 'textNode'  && <><Type      size={12} className="text-[#8c7db0]" /><span className="text-[12px] text-gray-700 dark:text-gray-300 font-medium">文本节点</span></>}
                {selectedNode.type === 'videoNode' && !!selectedNode.data?.url && (
                  <button onClick={() => extractAudioFromVideoNode(selectedNode)} disabled={extractBusy}
                    title="提取这段视频的声音，生成音频节点（可连给其它 Seedance 2.0 片段做参考音频，保持角色声线一致）"
                    className="ml-auto flex items-center gap-1 text-[11px] text-[#6f6196] dark:text-[#b9afe7] hover:bg-[#efe6fa] dark:hover:bg-white/5 transition-colors px-2 py-1 rounded-lg disabled:opacity-50">
                    {extractBusy ? <Loader2 size={12} className="animate-spin" /> : <Volume2 size={12} />}
                    提取声音
                  </button>
                )}
                {hasAsset && selectedNode.type !== 'textNode' && (
                  <button onClick={downloadAsset}
                    className={`${selectedNode.type === 'videoNode' ? '' : 'ml-auto'} text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5`}>
                    <Download size={13} />
                  </button>
                )}
                {(selectedNode.type === 'imageNode' || selectedNode.type === 'audioNode') && !!selectedNode.data?.url && (
                  <button onClick={() => depositToAssetLibrary(selectedNode)} disabled={depositBusy}
                    title={selectedNode.type === 'audioNode' ? '把声线存入资产库（跨画布复用）' : '存入资产库（账号级，跨画布跨剧复用）'}
                    className={`${hasAsset ? '' : 'ml-auto'} flex items-center gap-1 text-[11px] text-[#6f6196] dark:text-[#b9afe7] hover:bg-[#efe6fa] dark:hover:bg-white/5 transition-colors px-2 py-1 rounded-lg disabled:opacity-50`}>
                    {depositBusy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    {selectedNode.type === 'audioNode' ? '存入声线' : '存入资产库'}
                  </button>
                )}
                <button onClick={() => setSelectedId(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 ml-1">
                  <X size={13} />
                </button>
              </div>

              {/* ── 图片面板 ── */}
              {selectedNode.type === 'imageNode' && (
                <div className="p-3 space-y-2.5">
                  {imgTab && (
                    <>
                      {linkedText && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12]">
                          <Type size={11} className="text-[#6f6196] dark:text-[#b9afe7] shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] text-[#6f6196] dark:text-[#b9afe7] mb-0.5">已关联文本节点（自动作为提示词）</p>
                            <p className="text-[12px] text-gray-700 dark:text-gray-300 line-clamp-2">{linkedText}</p>
                          </div>
                        </div>
                      )}
                      <ReferenceMediaGroup
                        items={promptReferenceDisplayItems}
                        onInsert={item => setImgPrompt(current => `${current}${current && !/\s$/.test(current) ? ' ' : ''}@${item.alias} `)}
                        onRemove={item => cutEdge(item.edgeId)}
                      />
                      {refImageInputs.length > 0 && imgSpec?.provider !== 'dashscope' && (
                        <p className="text-[11px] text-amber-500">当前模型会把参考图作为通用图像参考；Qwen 支持专用图生图模式</p>
                      )}
                      <NodePromptEditor
                        value={imgPrompt}
                        onChange={setImgPrompt}
                        bindings={promptReferenceBindings}
                        referenceItems={promptReferenceDisplayItems}
                        onPersist={({ promptText }) => persistNodePrompt({ editorText: promptText }).catch(() => {})}
                        placeholder={linkedText ? '补充描述；输入 @ 可指定参考素材…' : '描述人物、场景；输入 @ 可指定参考素材…'}
                      />
                      {genErr && <p className="text-red-500 dark:text-red-400 text-[12px]">{genErr}</p>}
                      <input type="file" accept="image/*" ref={uploadRef} className="hidden" onChange={handleUpload} />
                      {/* 底栏：模型 chip + 设置 chip + 上传 + 生成 */}
                      <div className="relative flex items-center gap-2 flex-wrap">
                        <div className="relative">
                          <GenChip icon={Sparkles} active={imgMenu === 'model'} onClick={() => setImgMenu(m => m === 'model' ? null : 'model')}>{imgSpec?.label || '模型'}</GenChip>
                          <GenPopover open={imgMenu === 'model'} onClose={() => setImgMenu(null)} className="w-64">
                            {models.image.map(m => {
                              const sel = m.key === imgModelVal;
                              const isNano = m.key.startsWith('nano-banana');
                              return (
                                <button key={m.key} type="button" onClick={() => { setImgModel(m.key); setImgMenu(null); }}
                                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${sel ? 'bg-[#eee9de] dark:bg-white/[0.08]' : 'hover:bg-[#f5f0e7] dark:hover:bg-white/[0.04]'}`}>
                                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7]"><ImageIcon size={12} /></span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block text-[12px] text-gray-800 dark:text-gray-200">{m.label}</span>
                                    <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                      <span className="rounded bg-[#f2eadb] px-1.5 text-[10px] text-[#7f6b45] dark:bg-white/[0.06] dark:text-[#d4c7a8]">{m.creditsBySize ? `${m.cost}起` : m.cost}点</span>
                                      {m.sizes && <span className="text-[10px] text-gray-400">{m.sizes.join('/')}</span>}
                                      {m.provider === 'dashscope' && <span className="text-[10px] text-gray-400">图生图</span>}
                                      {isNano && <span className="rounded bg-emerald-100 px-1 text-[9px] font-medium text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">NEW</span>}
                                    </span>
                                  </span>
                                  {sel && <Check size={14} className="shrink-0 text-[#6f6196] dark:text-[#b9afe7]" />}
                                </button>
                              );
                            })}
                          </GenPopover>
                        </div>
                        <div className="relative">
                          <GenChip icon={SlidersHorizontal} active={imgMenu === 'settings'} onClick={() => setImgMenu(m => m === 'settings' ? null : 'settings')}>
                            {imgQualityVal ? `${imgQualityVal} · ` : ''}{imgRatioVal}{VIEWS[imgViewIdx]?.key !== 'free' ? ` · ${VIEWS[imgViewIdx].label}` : ''}
                          </GenChip>
                          <GenPopover open={imgMenu === 'settings'} onClose={() => setImgMenu(null)} className="w-60">
                            {imgSpec?.sizes && (
                              <>
                                <p className="px-1 pb-1.5 pt-1 text-[11px] text-gray-400">画质</p>
                                <div className="flex flex-wrap gap-1.5 px-1">
                                  {imgSpec.sizes.map(s => <GenPill key={s} active={imgQualityVal === s} onClick={() => setImgQuality(s)}>{s}</GenPill>)}
                                </div>
                              </>
                            )}
                            <p className="px-1 pb-1.5 pt-2.5 text-[11px] text-gray-400">比例</p>
                            <div className="flex flex-wrap gap-1.5 px-1">
                              {IMG_RATIOS.map(r => <GenPill key={r} active={imgRatioVal === r} onClick={() => setImgSize(RATIO_SIZE[r])}>{r}</GenPill>)}
                            </div>
                            <p className="px-1 pb-1.5 pt-2.5 text-[11px] text-gray-400">构图预设</p>
                            <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                              {VIEWS.map((v, i) => <GenPill key={v.key} active={imgViewIdx === i} onClick={() => setImgViewIdx(i)} title={v.prompt || undefined}>{v.label}</GenPill>)}
                            </div>
                          </GenPopover>
                        </div>
                        <button onClick={() => uploadRef.current?.click()} disabled={hasBlockingProductionGaps || uploading}
                          title="上传本地图片（JPG/PNG/WebP）"
                          className="flex items-center gap-1.5 rounded-lg border border-[#ded6c8] px-2.5 py-1.5 text-[12px] text-gray-600 hover:bg-[#f7f3eb] disabled:opacity-40 dark:border-white/[0.1] dark:text-gray-300 dark:hover:bg-white/[0.04]">
                          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}上传
                        </button>
                        <button onClick={generateImage} disabled={hasBlockingProductionGaps || isProcessing || (!imgPrompt.trim() && !linkedText && !VIEWS[imgViewIdx].prompt)}
                          className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#8c7db0] px-3.5 py-1.5 text-[12px] font-medium text-white hover:bg-[#7d6ca3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                          {hasBlockingProductionGaps
                            ? <><CircleAlert size={13} />先处理缺口</>
                            : isProcessing ? <><Loader2 size={13} className="animate-spin" />生成中…</> : <><Sparkles size={13} />生成{imgSpec ? ` · ${imgCost}点` : ''}</>}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── 视频面板 ── */}
              {selectedNode.type === 'videoNode' && (
                <div className="p-3 space-y-2.5">
                  <input ref={videoUploadRef} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" className="hidden" onChange={handleVideoUpload} />
                  {vidSpec?.provider === 'dashscope' && refImgNodes.length === 0 && (
                    <p className="text-[11px] text-amber-500">{vidSpec.label} 需至少连接一张参考图</p>
                  )}
                  {linkedVideoText && (
                    <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-[#eee9de] dark:bg-white/[0.08] border border-[#ded6c8] dark:border-white/[0.12]">
                      <Type size={11} className="text-[#6f6196] dark:text-[#b9afe7] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-[#6f6196] dark:text-[#b9afe7] mb-0.5">已关联文本节点（自动作为提示词）</p>
                        <p className="text-[12px] text-gray-700 dark:text-gray-300 line-clamp-2">{linkedVideoText}</p>
                      </div>
                    </div>
                  )}
                  <ReferenceMediaGroup
                    items={promptReferenceDisplayItems}
                    onInsert={item => setVidPrompt(current => `${current}${current && !/\s$/.test(current) ? ' ' : ''}@${item.alias} `)}
                    onRemove={item => cutEdge(item.edgeId)}
                  />
                  {refVideoAudioNodes.length > 0 && !supportsReferenceAudio && (
                    <p className="text-[11px] text-amber-500">当前模型不支持参考音频，请切换为支持音频参考的模型</p>
                  )}
                  {refAudioNeedsVisual && (
                    <p className="text-[11px] text-amber-500">当前模型要求参考音频必须同时配至少一张参考图或一段参考视频</p>
                  )}
                  {refVideoNodes.length > 0 && !supportsReferenceVideo && (
                    <p className="text-[11px] text-amber-500">当前模型不支持参考视频，请切换为支持视频参考的模型</p>
                  )}
                  {refVideoEmpty.length > 0 && (
                    <p className="text-[11px] text-amber-500">
                      有 {refVideoEmpty.length} 个连入的视频节点还没生成出视频，不能当参考。请先在那个节点上生成，或改连一段已生成好的片段。
                    </p>
                  )}
                  {/* 「还没存进我们的存储」不再提示：提交时后端会自己补存好，用户不需要知道这个内部搬运状态。
                      只有补存真的失败过（archive_error）才露出一个小红叹号，指针放上去说清要做什么。 */}
                  {refVideoArchiveFailed.length > 0 && (
                    <button
                      onClick={retryArchiveRefVideos}
                      disabled={archivingRefVideo}
                      title={archivingRefVideo ? '正在重试…' : '有参考视频没能存进我们的存储，24 小时后会失效。请重新上传这段视频，或点这里重试一次。'}
                      className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-600 disabled:opacity-50"
                    >
                      {archivingRefVideo
                        ? <Loader2 size={12} className="animate-spin" />
                        : <CircleAlert size={12} />}
                      <span>有 {refVideoArchiveFailed.length} 段参考视频需要重新上传</span>
                    </button>
                  )}
                  {refVideoOverLimit && (
                    <p className="text-[11px] text-amber-500">
                      {refVideoTooMany
                        ? `已连 ${refVideoNodes.length} 段参考视频，超过上限 3 段。请断开多余的连线。`
                        : refVideoTooLongClip
                          ? '有参考视频单段超过 15 秒，模型不接受。请换一段短的或先裁短。'
                          : `参考视频合计 ${(refVideoTotalMs / 1000).toFixed(1)}s，超过上限 15s。请断开或换短的片段。`}
                    </p>
                  )}
                  {refVideoNodes.length > 0 && refVideoEmpty.length === 0 && refVideoArchiveFailed.length === 0 && !refVideoOverLimit && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                      已连 {refVideoNodes.length} 段参考视频{refVideoTotalMs > 0 ? `（合计 ${(refVideoTotalMs / 1000).toFixed(1)}s / 上限 15s）` : ''}。想「延长」「改内容」还是「只借运镜风格」，请在提示词里写清楚——例如「接着 @{promptReferenceDisplayItems.find(i => i.mediaType === 'video')?.alias || '视频1'} 继续往后拍」。
                    </p>
                  )}
                  {/* 输入视频时长计入火山计费，点数会明显高于纯生成，先把差价讲明白再让用户点生成 */}
                  {refVideoNodes.length > 0 && !refVideoOverLimit && vidCost > vidOutputCost && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                      参考视频的时长也计入厂商计费，本次 {vidCost} 点（纯生成同参数为 {vidOutputCost} 点）。
                    </p>
                  )}
                  {refAudioOverLimit && (
                    <p className="text-[11px] text-amber-500">
                      {refAudioTooMany
                        ? `参考音频最多 ${Number(vidSpec?.maxReferenceAudios) || 3} 段，请断开多余的连线。`
                        : refAudioFormatInvalid
                          ? `当前模型的参考音频仅支持 ${allowedAudioFormats.map(value => value.toUpperCase()).join('、')} 格式。`
                          : refAudioDurationInvalid
                            ? `当前模型要求单段参考音频为 ${Number(vidSpec?.referenceAudioMinMs) / 1000 || 0}～${Number(vidSpec?.referenceAudioMaxMs) / 1000 || '∞'} 秒。`
                        : `参考音频合计 ${(refAudioTotalMs / 1000).toFixed(1)}s，超过当前模型上限 ${(REF_AUDIO_TOTAL_LIMIT_MS / 1000).toFixed(1)}s。请删掉或裁短部分参考音频后再生成。`}
                    </p>
                  )}
                  {refMediaTooMany && (
                    <p className="text-[11px] text-amber-500">图片、视频和音频合计最多 {maxReferenceFiles} 个参考文件，请断开多余的连线。</p>
                  )}
                  {refImageTooMany && (
                    <p className="text-[11px] text-amber-500">当前模型最多使用 {maxReferenceImages} 张参考图片，请断开多余的连线。</p>
                  )}
                  {refImgNodes.length === 0 && refVideoNodes.length === 0 && refVideoAudioNodes.length === 0 && (
                    <p className="text-[12px] text-gray-400 dark:text-gray-600">从图片、音频或已生成的视频节点右侧拖线连接，可作为视频参考</p>
                  )}
                  <NodePromptEditor
                    value={vidPrompt}
                    onChange={setVidPrompt}
                    bindings={promptReferenceBindings}
                    referenceItems={promptReferenceDisplayItems}
                    onPersist={({ promptText }) => persistNodePrompt({ editorText: promptText }).catch(() => {})}
                    placeholder="描述视频内容；输入 @ 可指定图片、音频或视频参考…"
                  />
                  {genErr && <p className="text-red-500 dark:text-red-400 text-[12px]">{genErr}</p>}
                  {isProcessing && (
                    <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 text-[12px]">
                      <Loader2 size={12} className="animate-spin" />视频生成中，通常需要 30–90 秒…
                    </div>
                  )}
                  {/* 底栏：模型 chip + 设置 chip + 生成 */}
                  <div className="relative flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <GenChip icon={Video} active={vidMenu === 'model'} onClick={() => setVidMenu(m => m === 'model' ? null : 'model')}>{vidSpec?.label || '模型'}</GenChip>
                      <GenPopover open={vidMenu === 'model'} onClose={() => setVidMenu(null)} className="w-64">
                        {models.video.map(m => {
                          const sel = m.key === vidModelVal;
                          return (
                            <button key={m.key} type="button" onClick={() => { setVidModel(m.key); setVidMenu(null); }}
                              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${sel ? 'bg-[#eee9de] dark:bg-white/[0.08]' : 'hover:bg-[#f5f0e7] dark:hover:bg-white/[0.04]'}`}>
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#eee9de] text-[#6f6196] dark:bg-white/[0.08] dark:text-[#b9afe7]"><Video size={12} /></span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-[12px] text-gray-800 dark:text-gray-200">{m.label}</span>
                                <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                                  {m.resolutions?.length > 0 && <span className="text-[10px] text-gray-400">{m.resolutions.join('/')}</span>}
                                  <span className="text-[10px] text-gray-400">按分辨率×时长</span>
                                </span>
                              </span>
                              {sel && <Check size={14} className="shrink-0 text-[#6f6196] dark:text-[#b9afe7]" />}
                            </button>
                          );
                        })}
                      </GenPopover>
                    </div>
                    <div className="relative">
                      <GenChip icon={SlidersHorizontal} active={vidMenu === 'settings'} onClick={() => setVidMenu(m => m === 'settings' ? null : 'settings')}>
                        {vidRatio} · {vidResVal || '—'} · {vidDurVal}s{supportsGeneratedAudio && vidAudio ? ' · 有声' : ''}
                      </GenChip>
                      <GenPopover open={vidMenu === 'settings'} onClose={() => setVidMenu(null)} className="w-64">
                        {vidSpec?.resolutions?.length > 0 && (
                          <>
                            <p className="px-1 pb-1.5 pt-1 text-[11px] text-gray-400">清晰度</p>
                            <div className="flex flex-wrap gap-1.5 px-1">
                              {vidSpec.resolutions.map(rsl => <GenPill key={rsl} active={vidResVal === rsl} onClick={() => setVidRes(rsl)} title={`${vidSpec.creditsPerSec?.[rsl]} 点/秒`}>{rsl}</GenPill>)}
                            </div>
                          </>
                        )}
                        <p className="px-1 pb-1.5 pt-2.5 text-[11px] text-gray-400">比例</p>
                        <div className="flex flex-wrap gap-1.5 px-1">
                          {RATIOS.map(r => <GenPill key={r} active={vidRatio === r} onClick={() => setVidRatio(r)}>{r}</GenPill>)}
                        </div>
                        <p className="px-1 pb-1.5 pt-2.5 text-[11px] text-gray-400">时长</p>
                        <div className="flex flex-wrap gap-1.5 px-1">
                          {DURATIONS.filter(d => d >= vidDurationMin && d <= vidDurationMax).map(d => <GenPill key={d} active={vidDurVal === d} onClick={() => setVidDur(d)}>{d}s</GenPill>)}
                        </div>
                        {supportsGeneratedAudio && <>
                          <p className="px-1 pb-1.5 pt-2.5 text-[11px] text-gray-400">生成音频</p>
                          <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                            <GenPill active={vidAudio} onClick={() => setVidAudio(true)}>开启</GenPill>
                            <GenPill active={!vidAudio} onClick={() => setVidAudio(false)}>关闭</GenPill>
                          </div>
                        </>}
                      </GenPopover>
                    </div>
                    <button onClick={() => videoUploadRef.current?.click()} disabled={hasBlockingProductionGaps || uploading || isProcessing}
                      title="上传外部视频当参考视频（mp4/mov，2–15 秒，≤50MB，不扣点）"
                      className="flex items-center gap-1.5 rounded-lg border border-[#ded6c8] px-2.5 py-1.5 text-[12px] text-gray-600 hover:bg-[#f7f3eb] disabled:opacity-40 dark:border-white/[0.1] dark:text-gray-300 dark:hover:bg-white/[0.04]">
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}上传视频
                    </button>
                    <button onClick={generateVideo} disabled={hasBlockingProductionGaps || isProcessing || refVideoAudioNodes.some(n => !n.data.url) || refAudioOverLimit || refAudioNeedsVisual || refMediaTooMany || refImageTooMany || (refVideoAudioNodes.length > 0 && !supportsReferenceAudio) || refVideoEmpty.length > 0 || refVideoOverLimit || (refVideoNodes.length > 0 && !supportsReferenceVideo)}
                      className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#8c7db0] px-3.5 py-1.5 text-[12px] font-medium text-white hover:bg-[#7d6ca3] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      {hasBlockingProductionGaps
                        ? <><CircleAlert size={13} />先处理缺口</>
                        : isProcessing
                        ? <><Loader2 size={13} className="animate-spin" />生成中…</>
                        : <><Sparkles size={13} />生成{vidCost ? ` · ${vidCost}点` : ''}</>}
                    </button>
                  </div>
                </div>
              )}

              {/* ── 音频面板 ── */}
              {selectedNode.type === 'audioNode' && (
                <div className="space-y-2.5 p-3">
                  {linkedAudioText && (
                    <div className="flex items-start gap-2 rounded-xl border border-[#ded6c8] bg-[#eee9de] px-3 py-2 dark:border-white/[0.12] dark:bg-white/[0.08]">
                      <Type size={11} className="mt-0.5 shrink-0 text-[#6f6196] dark:text-[#b9afe7]" />
                      <div className="min-w-0 flex-1">
                        <p className="mb-0.5 text-[10px] text-[#6f6196] dark:text-[#b9afe7]">已关联文本节点（自动作为台词）</p>
                        <p className="line-clamp-2 text-[12px] text-gray-700 dark:text-gray-300">{linkedAudioText}</p>
                      </div>
                    </div>
                  )}

                  {refAudioVoiceNodes.length > 0 && (
                    <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-400/20 dark:bg-violet-500/10">
                      <p className="text-[10px] text-violet-500">参考声音</p>
                      {refAudioVoiceNodes.map(node => (
                        <p key={node.id} className="mt-1 flex items-center gap-1.5 text-[11px] text-violet-700 dark:text-violet-200">
                          <Volume2 size={11} />
                          <span className="truncate">{node.data.label || '音频样本'}</span>
                          <span className="ml-auto shrink-0">{node.data.voice_id ? '声线已锁定 ✓' : '请先注册声线'}</span>
                        </p>
                      ))}
                    </div>
                  )}

                  {selectedNode.data.voice_id && (
                    <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-[11px] text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <CheckCircle size={13} />此节点已拥有固定声音身份，后续台词会保持同一声线
                    </div>
                  )}

                  <textarea
                    value={audText}
                    onChange={event => setAudText(event.target.value)}
                    placeholder={linkedAudioText ? '补充台词（可选）…' : '输入要生成的台词…'}
                    rows={3}
                    className="w-full resize-none rounded-xl border border-[#ded6c8] bg-[#f7f3eb] px-3 py-2.5 text-[13px] text-gray-800 placeholder-gray-400 outline-none transition-colors focus:border-[#8c7db0] dark:border-white/[0.08] dark:bg-black/25 dark:text-gray-200 dark:placeholder-gray-700"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <label className="space-y-1 text-[10px] text-gray-400">
                      <span>音频模型</span>
                      <select value={audModelVal} onChange={event => setAudModel(event.target.value)}
                        className="w-full rounded-lg border border-[#ded6c8] bg-[#f7f3eb] px-2 py-2 text-[11px] text-gray-700 outline-none dark:border-white/[0.08] dark:bg-black/20 dark:text-gray-300">
                        {(models.audio || []).map(model => <option key={model.key} value={model.key}>{model.label}</option>)}
                      </select>
                    </label>
                    <label className="space-y-1 text-[10px] text-gray-400">
                      <span>{refAudioVoiceNodes.length || selectedNode.data.voice_id ? '当前声线' : '系统音色'}</span>
                      {refAudioVoiceNodes.length || selectedNode.data.voice_id ? (
                        <div className="truncate rounded-lg border border-[#ded6c8] bg-[#f7f3eb] px-2 py-2 text-[11px] text-gray-700 dark:border-white/[0.08] dark:bg-black/20 dark:text-gray-300">固定角色声线</div>
                      ) : (
                        <select value={audVoiceVal} onChange={event => setAudVoice(event.target.value)}
                          className="w-full rounded-lg border border-[#ded6c8] bg-[#f7f3eb] px-2 py-2 text-[11px] text-gray-700 outline-none dark:border-white/[0.08] dark:bg-black/20 dark:text-gray-300">
                          {(audSpec?.voices || []).map(voice => <option key={voice.key} value={voice.key}>{voice.label}</option>)}
                        </select>
                      )}
                    </label>
                  </div>

                  {genErr && <p className="text-[12px] text-red-500 dark:text-red-400">{genErr}</p>}
                  <input ref={audioUploadRef} type="file" accept="audio/wav,audio/mpeg,audio/mp3,audio/ogg,audio/opus" className="hidden" onChange={handleAudioUpload} />

                  {hasAsset && !selectedNode.data.voice_id && (
                    <div className="flex items-center gap-2 rounded-xl border border-[#ded6c8] px-3 py-2 dark:border-white/[0.08]">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-[10px] leading-4 text-gray-500 dark:text-gray-400">
                        <input type="checkbox" checked={voiceRightsConfirmed} onChange={event => setVoiceRightsConfirmed(event.target.checked)} className="mt-0.5" />
                        我确认拥有此声音样本的使用与复刻授权
                      </label>
                      <button type="button" onClick={registerCurrentVoice} disabled={!voiceRightsConfirmed || generating}
                        className="shrink-0 rounded-lg border border-violet-200 px-2.5 py-1.5 text-[11px] text-violet-600 hover:bg-violet-50 disabled:opacity-40 dark:border-violet-400/25 dark:text-violet-300 dark:hover:bg-violet-500/10">
                        {generating ? '注册中…' : '创建声音身份'}
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => audioUploadRef.current?.click()} disabled={uploading || generating}
                      className="flex items-center gap-1.5 rounded-lg border border-[#ded6c8] px-2.5 py-1.5 text-[12px] text-gray-600 hover:bg-[#f7f3eb] disabled:opacity-40 dark:border-white/[0.1] dark:text-gray-300 dark:hover:bg-white/[0.04]">
                      {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}上传音频
                    </button>
                    <span className="text-[10px] text-gray-400">WAV/MP3/OGG · ≤10MB</span>
                    <button type="button" onClick={generateAudioNode}
                      disabled={isProcessing || uploading || !audTextForGeneration || !audSpec || refAudioVoiceNodes.some(node => !node.data.voice_id)}
                      className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#8c7db0] px-3.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#7d6ca3] disabled:cursor-not-allowed disabled:opacity-40">
                      {isProcessing ? <><Loader2 size={13} className="animate-spin" />生成中…</> : <><Volume2 size={13} />生成语音{audCost ? ` · ${audCost}点` : ''}</>}
                    </button>
                  </div>
                </div>
              )}

              {/* ── 文本面板 ── */}
              {selectedNode.type === 'textNode' && (
                <div className="p-3 space-y-2.5">
                  <textarea
                    value={txtContent}
                    onChange={e => setTxtContent(e.target.value)}
                    onBlur={saveText}
                    placeholder="输入文字内容（剧本、广告词、画面描述…）"
                    rows={5}
                    className="w-full bg-[#f7f3eb] dark:bg-black/25 border border-[#ded6c8] dark:border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-700 resize-none focus:outline-none focus:border-[#8c7db0] transition-colors"
                  />
                  {genErr && <p className="text-red-500 dark:text-red-400 text-[12px]">{genErr}</p>}
                  <div className="flex gap-2">
                    <button onClick={saveText}
                      className="px-4 py-2.5 rounded-xl border border-[#ded6c8] dark:border-white/[0.1] text-gray-600 dark:text-gray-300 text-[13px] font-medium hover:bg-[#eee9de] dark:hover:bg-white/[0.04] transition-colors">
                      保存
                    </button>
                    <button onClick={createImageFromText} disabled={hasBlockingProductionGaps || generating || !txtContent.trim()}
                      className="flex-1 py-2.5 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-medium flex items-center justify-center gap-2 transition-colors">
                      {hasBlockingProductionGaps ? <><CircleAlert size={13} />先处理缺口</> : <><ImageIcon size={13} />生成图片</>}
                    </button>
                    <button onClick={createAudioFromText} disabled={generating || !txtContent.trim()}
                      className="flex-1 py-2.5 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-medium flex items-center justify-center gap-2 transition-colors">
                      <Volume2 size={13} />生成音频
                    </button>
                    <button onClick={createVideoFromText} disabled={hasBlockingProductionGaps || generating || !txtContent.trim()}
                      className="flex-1 py-2.5 rounded-xl bg-[#8c7db0] hover:bg-[#7d6ca3] disabled:opacity-40 disabled:cursor-not-allowed text-white text-[13px] font-medium flex items-center justify-center gap-2 transition-colors">
                      {hasBlockingProductionGaps ? <><CircleAlert size={13} />先处理缺口</> : <><Video size={13} />生成视频</>}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 dark:text-gray-600">会创建对应节点并连线，在新节点里选模型、点生成即可</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        <CanvasAgentPanel
          agent={canvasAgent}
          canvasName={canvasName}
          selectedNode={selectedNode}
          nodes={nodes}
          productionPlan={productionPlan}
          deliveryManifest={deliveryManifest}
          imageModelLabel={imgSpec ? `${imgSpec.label} · ${imgSpec.model}` : ''}
          videoModelLabel={vidSpec ? `${vidSpec.label} · ${vidSpec.model}` : ''}
          imageModels={models.image}
          imageModelValue={imgModelVal}
          onImageModelChange={setImgModel}
          artStyles={artStyles}
          artStyleValue={agentStyle}
          onArtStyleChange={setAgentStyle}
          videoModels={models.video}
          videoModelValue={vidModelVal}
          videoResolutions={vidSpec?.resolutions || []}
          videoResolution={vidResVal}
          videoGenerateAudio={vidAudio}
          videoSupportsGeneratedAudio={supportsGeneratedAudio}
          onVideoModelChange={setVidModel}
          onVideoResolutionChange={setVidRes}
          onVideoGenerateAudioChange={setVidAudio}
          notice={agentNotice}
          error={agentErr}
          onConfirmNodes={confirmAgentNodes}
          onPrepareAction={prepareAgentAction}
          onRefreshPlan={refreshProductionPlan}
          onRefreshDelivery={refreshDeliveryManifest}
          onArchiveDelivery={archiveDeliveryVideos}
          onLockDelivery={lockDeliveryVideos}
          onSelectedQc={updateSelectedAgentQc}
          onUploadImageFiles={uploadImageFilesToCanvas}
          onGenerateGapReference={generateGapReference}
          onUploadGapReference={uploadGapReference}
          onResetGapReference={resetGap}
          onSkipGapReference={skipGap}
          onSetNodeAsAsset={setNodeAsAsset}
          onGenerateAllAssets={generateAgentAssets}
          generatingGapIds={generatingGapIds}
        />

        {showLegacyAgentPanel && (
        <aside className="w-[390px] shrink-0 border-l border-[#ded6c8] bg-[#fbf8f1] dark:bg-[#0c0c10] dark:border-white/[0.06] flex flex-col">
          <div className="h-[88px] px-5 border-b border-[#e4dccf] dark:border-white/[0.06] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border-2 border-[#9fb0bb] bg-white dark:bg-white/[0.05]" />
            <div className="min-w-0">
              <p className="text-[16px] font-bold text-stone-900 dark:text-gray-100 truncate">剧本创作 · 画布 Agent</p>
              <p className="text-[12px] text-stone-400">副导演 · 场记 · 美术助理</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            <div className="rounded-lg border border-[#d9d0c2] bg-[#f4f1ec] px-4 py-3 text-[14px] leading-7 text-stone-800">
              把剧本拆成分镜，并把人物、场景、道具和招数特效编织到画布里。
            </div>

            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Wand2 size={15} className="text-[#6f6196]" />
                <p className="text-[13px] font-semibold text-stone-800">当前建议</p>
              </div>
              <p className="text-[13px] leading-7 text-stone-700">
                先把剧本节点、角色参考和场景参考连好，再生成首帧。已生成的视频会回写到对应节点。
              </p>
            </section>

            <section className="space-y-2">
              <p className="text-[12px] font-semibold text-stone-400">生产检查</p>
              {[
                ['读取画布', nodes.length > 0 ? '已就绪' : '等待节点'],
                ['资产引用', assetRows.length > 0 ? `${assetRows.length} 个` : '待补充'],
                ['分镜任务', shotRows.length > 0 ? `${shotRows.length} 个` : '未拆分'],
                ['质检可用', qcReadyAssetCount > 0 ? `${qcReadyAssetCount} 个` : '待确认'],
                ['需重生', qcNeedsRegenCount > 0 ? `${qcNeedsRegenCount} 个` : '无'],
                ['首帧可用', keyframeReadyCount > 0 ? `${keyframeReadyCount} 个` : keyframeRows.length ? '待确认' : '未生成'],
                ['首帧重生', keyframeNeedsRegenCount > 0 ? `${keyframeNeedsRegenCount} 个` : '无'],
                ['视频任务', videoTaskText],
              ].map(([label, value]) => (
                <div key={label} className="h-10 rounded-lg border border-[#e1d9ca] bg-white/70 flex items-center px-3">
                  <span className="text-[13px] text-stone-700">{label}</span>
                  <span className="ml-auto text-[12px] text-stone-400">{value}</span>
                </div>
              ))}
            </section>

            {latestAgentAnalysis?.reliability && (
              <section className="space-y-2">
                <p className="text-[12px] font-semibold text-stone-400">分析可靠性</p>
                <div className="h-10 rounded-lg border border-[#e1d9ca] bg-white/70 flex items-center px-3">
                  <span className="text-[13px] text-stone-700">分析方式</span>
                  <span className="ml-auto text-[12px] text-stone-400">
                    {latestAgentAnalysis.analysis_source === 'llm' || latestAgentAnalysis.source === 'llm' ? 'LLM' : '规则兜底'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['人物', latestAgentAnalysis.reliability.character],
                    ['场景', latestAgentAnalysis.reliability.scene],
                    ['道具', latestAgentAnalysis.reliability.prop],
                    ['招数', latestAgentAnalysis.reliability.fx_move],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-[#e1d9ca] bg-white/70 px-3 py-2">
                      <p className="text-[12px] text-stone-500">{label}</p>
                      <p className="text-[13px] font-semibold text-stone-800">{value}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {selectedNode?.data?.agent_role === 'asset' && (
              <section className="space-y-2">
                <p className="text-[12px] font-semibold text-stone-400">资产质检</p>
                <div className="rounded-lg border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 space-y-1">
                  <div className="flex items-center">
                    <span>当前资产</span>
                    <span className="ml-auto text-stone-800 font-semibold truncate max-w-[210px]">{selectedNode.data?.label || '资产'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>质检状态</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.qc_status || 'pending'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>参考图</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.url ? '已有' : '缺失'}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => updateAssetQc('usable', '资产可进入首帧')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><CheckCircle size={13} />标为可用</span>
                  </button>
                  <button onClick={() => updateAssetQc('needs_regen', '资产需要重生')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><RefreshCw size={13} />需重生</span>
                  </button>
                  <button onClick={() => updateAssetQc('locked', '资产已锁定为生产参考')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><Shield size={13} />锁定资产</span>
                  </button>
                  <button onClick={() => updateAssetQc('rejected', '资产已拒绝', 'rejected')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><X size={13} />拒绝</span>
                  </button>
                </div>
                <button
                  onClick={regenerateSelectedAsset}
                  disabled={assetRefLoading || !['needs_regen', 'retry_recommended'].includes(selectedNode.data?.qc_status)}
                  className="w-full rounded-lg border border-[#ded6c8] bg-[#eee9de] px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{assetRefLoading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}重生当前资产</span>
                  <span className="mt-1 block text-[11px] text-stone-400">只处理标记为需重生的当前资产</span>
                </button>
              </section>
            )}

            {selectedNode?.data?.agent_role === 'keyframe' && (
              <section className="space-y-2">
                <p className="text-[12px] font-semibold text-stone-400">首帧质检</p>
                <div className="rounded-lg border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 space-y-1">
                  <div className="flex items-center">
                    <span>当前首帧</span>
                    <span className="ml-auto text-stone-800 font-semibold truncate max-w-[210px]">{selectedNode.data?.label || '首帧'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>质检状态</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.qc_status || 'pending'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>首帧图</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.url ? '已有' : '缺失'}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => updateKeyframeQc('usable', '首帧可进入视频提交')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><CheckCircle size={13} />标为可用</span>
                  </button>
                  <button onClick={() => updateKeyframeQc('needs_regen', '首帧需要重生')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><RefreshCw size={13} />需重生</span>
                  </button>
                  <button onClick={() => updateKeyframeQc('locked', '首帧已锁定为视频参考')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><Shield size={13} />锁定首帧</span>
                  </button>
                  <button onClick={() => updateKeyframeQc('rejected', '首帧已拒绝', 'rejected')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><X size={13} />拒绝</span>
                  </button>
                </div>
                <button
                  onClick={regenerateSelectedKeyframe}
                  disabled={keyframeLoading || !['needs_regen', 'retry_recommended'].includes(selectedNode.data?.qc_status)}
                  className="w-full rounded-lg border border-[#ded6c8] bg-[#eee9de] px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{keyframeLoading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}重生当前首帧</span>
                  <span className="mt-1 block text-[11px] text-stone-400">只处理标记为需重生的当前首帧</span>
                </button>
              </section>
            )}

            {['clip', 'video_clip'].includes(selectedNode?.data?.agent_role) && (
              <section className="space-y-2">
                <p className="text-[12px] font-semibold text-stone-400">视频质检</p>
                <div className="rounded-lg border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 space-y-1">
                  <div className="flex items-center">
                    <span>当前视频</span>
                    <span className="ml-auto text-stone-800 font-semibold truncate max-w-[210px]">{selectedNode.data?.label || '视频'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>生成状态</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.status || 'pending'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>质检状态</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.qc_status || 'pending'}</span>
                  </div>
                  <div className="flex items-center">
                    <span>成片</span>
                    <span className="ml-auto text-stone-400">{selectedNode.data?.url ? '已有' : selectedNode.data?.taskId ? '处理中' : '未提交'}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => updateVideoQc('usable', '视频可进入交付候选')}
                    disabled={!selectedNode.data?.url}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><CheckCircle size={13} />标为可用</span>
                  </button>
                  <button onClick={() => updateVideoQc('needs_regen', '视频需要重试')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><RefreshCw size={13} />需重试</span>
                  </button>
                  <button onClick={() => updateVideoQc('locked', '视频已锁定为交付候选')}
                    disabled={!selectedNode.data?.url}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><Shield size={13} />锁定视频</span>
                  </button>
                  <button onClick={() => updateVideoQc('rejected', '视频已拒绝', 'rejected')}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]">
                    <span className="flex items-center gap-1.5 font-semibold"><X size={13} />拒绝</span>
                  </button>
                </div>
                <button
                  onClick={regenerateSelectedVideo}
                  disabled={videoSubmitLoading || !['needs_regen', 'retry_recommended'].includes(selectedNode.data?.qc_status) || selectedNode.data?.status === 'processing'}
                  className="w-full rounded-lg border border-[#ded6c8] bg-[#eee9de] px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{videoSubmitLoading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}重提当前视频</span>
                  <span className="mt-1 block text-[11px] text-stone-400">只处理标记为需重试的当前视频</span>
                </button>
              </section>
            )}

            <section className="space-y-2">
              <div className="flex items-center">
                <p className="text-[12px] font-semibold text-stone-400">生产队列</p>
                <button
                  onClick={refreshProductionPlan}
                  disabled={productionPlanLoading}
                  className="ml-auto rounded-lg border border-[#ded6c8] bg-white/80 px-2.5 py-1.5 text-[11px] text-stone-600 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  {productionPlanLoading ? '检查中' : '刷新预检'}
                </button>
              </div>
              <div className="rounded-lg border border-[#ded6c8] bg-white/70 p-3 text-[12px] text-stone-600 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">待执行</p>
                    <p className="font-semibold text-stone-800">{productionPlanItems.length}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">预计点数</p>
                    <p className="font-semibold text-stone-800">{productionPlan?.totals?.totalCost ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">余额</p>
                    <p className={`font-semibold ${productionPlan?.affordable === false ? 'text-rose-600' : 'text-stone-800'}`}>{productionPlan?.credits ?? '—'}</p>
                  </div>
                </div>
                {productionPlanItems.length > 0 ? (
                  <div className="space-y-1.5">
                    {productionPlanItems.slice(0, 5).map(item => (
                      <div key={`${item.action}-${item.nodeId}`} className="flex items-center gap-2 rounded-lg border border-[#e7dfd0] bg-white/70 px-2 py-1.5">
                        <span className="truncate text-stone-700">{item.label}</span>
                        <span className="ml-auto shrink-0 text-[11px] text-stone-400">{item.cost}点</span>
                      </div>
                    ))}
                    {productionPlanItems.length > 5 && <p className="text-[11px] text-stone-400">还有 {productionPlanItems.length - 5} 项未显示</p>}
                  </div>
                ) : (
                  <p className="text-[12px] text-stone-400">暂无待执行项，或请先刷新预检。</p>
                )}
                {productionSkippedItems.length > 0 && (
                  <p className="text-[11px] text-amber-600">{productionSkippedItems.length} 项被门禁跳过：{productionSkippedItems[0].reason}</p>
                )}
                {hasBlockingProductionGaps && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-5 text-amber-700">
                    有 {productionBlockedGaps.length} 项素材缺口阻塞生产。请先在 Agent 面板处理缺口；处理前不会开放真实生成。
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2 pt-1">
                  <button
                    onClick={generateAgentAssets}
                    disabled={hasBlockingProductionGaps || assetRefLoading || !(productionPlan?.counts?.assets > 0)}
                    className="rounded-lg border border-[#ded6c8] bg-[#eee9de] px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                  >
                    {assetRefLoading ? '生成中' : `资产 ${productionPlan?.totals?.assetCost || 0}点`}
                  </button>
                  <button
                    onClick={generateAgentKeyframes}
                    disabled={hasBlockingProductionGaps || keyframeLoading || !(productionPlan?.counts?.keyframes > 0)}
                    className="rounded-lg border border-[#ded6c8] bg-[#eee9de] px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                  >
                    {keyframeLoading ? '生成中' : `首帧 ${productionPlan?.totals?.keyframeCost || 0}点`}
                  </button>
                  <button
                    onClick={submitAgentVideos}
                    disabled={hasBlockingProductionGaps || videoSubmitLoading || !(productionPlan?.counts?.videos > 0)}
                    className="rounded-lg border border-[#ded6c8] bg-[#eee9de] px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                  >
                    {videoSubmitLoading ? '提交中' : `视频 ${productionPlan?.totals?.videoCost || 0}点`}
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-[12px] font-semibold text-stone-400">片段进度</p>
              <div className="rounded-lg overflow-hidden border border-[#ded6c8] bg-white/70">
                <div className="grid grid-cols-[1fr_52px_64px_64px_42px] gap-1 text-[11px] font-semibold text-stone-500 bg-[#f0ebe2] px-2 py-2">
                  <span>片段</span><span>资产</span><span>首帧</span><span>视频</span><span>点</span>
                </div>
                {shotProgressRows.slice(0, 6).map(row => (
                  <div key={row.id} className="grid grid-cols-[1fr_52px_64px_64px_42px] gap-1 border-t border-[#e8dfd0] px-2 py-2 text-[11px] text-stone-600">
                    <span className="truncate">{row.label}</span>
                    <span>{row.asset}</span>
                    <span className="truncate">{row.keyframe}</span>
                    <span className="truncate">{row.video}</span>
                    <span>{row.cost}</span>
                  </div>
                ))}
                {!shotProgressRows.length && <p className="px-3 py-3 text-[12px] text-stone-400">暂无片段。</p>}
              </div>
              {shotProgressRows.some(row => row.blocker) && (
                <p className="text-[11px] text-stone-400">当前阻塞：{shotProgressRows.find(row => row.blocker)?.blocker}</p>
              )}
            </section>

            <section className="space-y-2">
              <div className="flex items-center">
                <p className="text-[12px] font-semibold text-stone-400">项目交付</p>
                <button
                  onClick={refreshDeliveryManifest}
                  disabled={deliveryLoading}
                  className="ml-auto rounded-lg border border-[#ded6c8] bg-white/80 px-2.5 py-1.5 text-[11px] text-stone-600 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  {deliveryLoading ? '刷新中' : '刷新清单'}
                </button>
              </div>
              <div className="rounded-lg border border-[#ded6c8] bg-white/70 p-3 text-[12px] text-stone-600 space-y-2">
                <div className="grid grid-cols-4 gap-2">
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">视频</p>
                    <p className="font-semibold text-stone-800">{deliveryManifest?.summary?.total ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">可交付</p>
                    <p className="font-semibold text-stone-800">{deliveryManifest?.summary?.deliverable ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">已入库</p>
                    <p className="font-semibold text-stone-800">{deliveryManifest?.summary?.archived ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-[#f7f2e9] px-2 py-1.5">
                    <p className="text-[11px] text-stone-400">锁定</p>
                    <p className="font-semibold text-stone-800">{deliveryManifest?.summary?.locked ?? 0}</p>
                  </div>
                </div>
                {(deliveryManifest?.videos || []).slice(0, 4).map(video => (
                  <div key={video.nodeId} className="flex items-center gap-2 rounded-lg border border-[#e7dfd0] bg-white/70 px-2 py-1.5">
                    <span className="truncate text-stone-700">{video.label}</span>
                    <span className="ml-auto shrink-0 text-[11px] text-stone-400">{video.delivery_locked ? '已锁定' : video.archived ? '已入库' : video.qc_status}</span>
                  </div>
                ))}
                {!deliveryManifest && <p className="text-[12px] text-stone-400">刷新后查看项目交付包、镜序清单和成片入库状态。</p>}
                <button
                  type="button"
                  disabled
                  title="等待项目素材包导出接口接入"
                  className="w-full rounded-lg border border-[#ded6c8] bg-[#f4f1ec] px-2 py-2 text-[11px] text-stone-400 opacity-70"
                >
                  导出项目素材包 · 待接入
                </button>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button
                    onClick={archiveDeliveryVideos}
                    disabled={deliveryLoading || !(deliveryManifest?.summary?.pending_archive > 0)}
                    className="rounded-lg border border-[#ded6c8] bg-[#eee9de] px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#e7dfd0]"
                  >
                    {deliveryLoading ? '入库中' : `交付成片入库 ${deliveryManifest?.summary?.pending_archive || 0}`}
                  </button>
                  <button
                    onClick={lockDeliveryVideos}
                    disabled={deliveryLoading || !(deliveryManifest?.summary?.archived > 0) || deliveryManifest?.summary?.locked >= deliveryManifest?.summary?.deliverable}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                  >
                    <span className="inline-flex items-center gap-1"><Shield size={12} />锁定交付</span>
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => downloadDeliveryPackage('json')}
                    disabled={deliveryLoading || !deliveryManifest}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                  >
                    <span className="inline-flex items-center gap-1"><Download size={12} />交付包</span>
                  </button>
                  <button
                    onClick={() => downloadDeliveryPackage('csv')}
                    disabled={deliveryLoading || !deliveryManifest}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                  >
                    <span className="inline-flex items-center gap-1"><Download size={12} />镜序清单</span>
                  </button>
                  <button
                    onClick={() => downloadDeliveryPackage('report')}
                    disabled={deliveryLoading || !deliveryManifest}
                    className="rounded-lg border border-[#ded6c8] bg-white/80 px-2 py-2 text-[11px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                  >
                    <span className="inline-flex items-center gap-1"><Download size={12} />交付报告</span>
                  </button>
                </div>
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-[12px] font-semibold text-stone-400">生产动作</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={confirmAgentNodes}
                  disabled={hasBlockingProductionGaps || confirming}
                  className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{confirming ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}{hasBlockingProductionGaps ? '先处理缺口' : '确认待生成'}</span>
                  <span className="mt-1 block text-[11px] text-stone-400">{shotRows.filter(n => ['needs_confirm', 'missing'].includes(n.data?.status)).length + assetRows.filter(n => ['needs_confirm', 'missing'].includes(n.status)).length} 个待确认</span>
                </button>
                <button
                  onClick={generateAgentAssets}
                  disabled={hasBlockingProductionGaps || assetRefLoading || pendingAssetRefCount === 0}
                  className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{assetRefLoading ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}生成资产</span>
                  <span className="mt-1 block text-[11px] text-stone-400">{pendingAssetRefCount} 个待参考</span>
                </button>
                <button
                  onClick={generateAgentKeyframes}
                  disabled={hasBlockingProductionGaps || keyframeLoading || acceptedShotCount === 0 || pendingKeyframeCount === 0}
                  className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{keyframeLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}生成首帧</span>
                  <span className="mt-1 block text-[11px] text-stone-400">{pendingKeyframeCount} 个待生成</span>
                </button>
                <button
                  onClick={submitAgentVideos}
                  disabled={hasBlockingProductionGaps || videoSubmitLoading || acceptedShotCount === 0 || readyClipCount === 0}
                  className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2.5 text-left text-[12px] text-stone-700 disabled:opacity-50 hover:bg-[#f5f0e7]"
                >
                  <span className="flex items-center gap-1.5 font-semibold">{videoSubmitLoading ? <Loader2 size={13} className="animate-spin" /> : <Video size={13} />}提交视频</span>
                  <span className="mt-1 block text-[11px] text-stone-400">{readyClipCount} 个待提交</span>
                </button>
                <button
                  onClick={showRetryAdvice}
                  className="rounded-lg border border-[#ded6c8] bg-white/80 px-3 py-2.5 text-left text-[12px] text-stone-700 hover:bg-[#f5f0e7]"
                >
                  <span className="flex items-center gap-1.5 font-semibold"><Clock size={13} />失败重试建议</span>
                  <span className="mt-1 block text-[11px] text-stone-400">{retryRows.length} 个需检查</span>
                </button>
              </div>
            </section>

            <section>
              <p className="text-[12px] font-semibold text-stone-400 mb-2">分镜草表</p>
              <div className="rounded-lg overflow-hidden border border-[#ded6c8] bg-white/70">
                <div className="grid grid-cols-[64px_70px_1fr] text-[12px] font-semibold text-stone-500 bg-[#f0ebe2] px-3 py-2">
                  <span>片段</span><span>状态</span><span>内容</span>
                </div>
                {nodeRows.filter(n => n.type === 'videoNode').slice(0, 5).map((n, i) => (
                  <button key={n.id} onClick={() => setSelectedId(n.id)}
                    className="grid grid-cols-[64px_70px_1fr] w-full text-left text-[12px] text-stone-700 px-3 py-2 border-t border-[#eee6d8] hover:bg-[#f5f0e7]">
                    <span>{i + 1}</span>
                    <span>{statusText(n.status)}</span>
                    <span className="truncate">{n.label}</span>
                  </button>
                ))}
                {nodeRows.filter(n => n.type === 'videoNode').length === 0 && (
                  <div className="px-3 py-5 text-[12px] text-stone-400">还没有视频节点。</div>
                )}
              </div>
            </section>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => setAgentPrompt('把这场戏按人物单人反应镜头拆开，不要同框。')}
                className="rounded-full border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 flex items-center gap-1"><User size={13} /> 人物怎么站</button>
              <button onClick={() => setAgentPrompt('把整体气氛调松一点，保留冲突但减少压迫感。')}
                className="rounded-full border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 flex items-center gap-1"><Clock size={13} /> 气氛松一点</button>
              <button onClick={() => setAgentPrompt('基于当前画布，建议接下来补哪几个片段。')}
                className="rounded-full border border-[#ded6c8] bg-white/70 px-3 py-2 text-[12px] text-stone-600 flex items-center gap-1"><Play size={13} /> 接下来补哪里</button>
            </div>
            {(agentNotice || agentErr) && (
              <div className={`rounded-lg border px-3 py-2 text-[12px] leading-5 ${
                agentErr
                  ? 'border-red-200 bg-red-50 text-red-600'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}>
                {agentErr || agentNotice}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-[#e4dccf] dark:border-white/[0.06]">
            <div className="rounded-lg border border-[#d9d0c2] bg-white/80 p-3">
              <textarea
                value={agentPrompt}
                onChange={e => setAgentPrompt(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    runCanvasAgentStoryboard();
                  }
                }}
                rows={3}
                placeholder="粘贴一段剧本或描述需求 · Ctrl/⌘ + Enter 建图"
                className="w-full resize-none bg-transparent text-[13px] text-stone-800 placeholder:text-stone-400 outline-none"
              />
              <div className="flex items-center pt-2">
                <button
                  onClick={confirmAgentNodes}
                  disabled={confirming}
                  className="rounded-lg border border-[#ded6c8] bg-[#eee9de] disabled:opacity-50 px-3 py-1.5 text-[12px] text-stone-600 flex items-center gap-1.5"
                >
                  {confirming ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={13} />}
                  手动确认
                </button>
                <button
                  onClick={runCanvasAgentStoryboard}
                  disabled={agentLoading || !agentPrompt.trim()}
                  className="ml-auto h-9 w-9 rounded-lg bg-[#8c7db0] disabled:opacity-40 text-white flex items-center justify-center shadow-lg shadow-[#8c7db0]/20"
                  title="拆镜并创建画布节点"
                >
                  {agentLoading ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}
                </button>
              </div>
            </div>
          </div>
        </aside>
        )}
      </div>
    </div>
  );
}
